/**
 * Betriebskosten Detail
 * ======================
 * Vollständige BKA-Verwaltung für eine Periode:
 * - Kopfleiste mit Objekt, Zeitraum, Status, Modus, Sperr-Button
 * - Tab „Positionen": Kostenaufstellung, Belegzustand, Add/Edit/Delete
 * - Tab „Belege": Upload, Multi-Verknüpfung (n:m mit Teilbeträgen)
 * - Tab „Einzelabrechnungen": (Re-)berechnen, PDF-Export je Mieter
 * - Tab „SEG-Import": Übernahme-Assistent aus fremder WEG-Abrechnung
 */
import { OilHeatingDialog } from "@/components/bka/OilHeatingDialog";
import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate, tankFuelOf } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, Plus, Trash, LockKey, LockKeyOpen, FilePdf, FilePlus,
  Receipt, CheckCircle, Warning, ArrowClockwise, Upload, X, Calculator,
  Download, FileText, ArrowsInLineHorizontal, Copy, ListChecks, CloudArrowUp,
  DotsSixVertical, Eye, Paperclip, CaretRight, CaretDown, PencilSimple,
  EnvelopeSimple, UsersFour,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PeriodMasterDataDialog from "@/components/bka/PeriodMasterDataDialog";
import { HeatingAdjustButton } from "@/components/bka/HeatingAdjustDialog";
import { openAuthenticatedPdf, fetchPdfBlobUrl } from "@/lib/pdfUtils";

const STATUS_META = {
  draft:  { label: "Entwurf",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
  locked: { label: "Abgeschlossen", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  sent:   { label: "Versendet",     cls: "bg-blue-100 text-blue-800 border-blue-200" },
  booked: { label: "Verbucht",      cls: "bg-purple-100 text-purple-800 border-purple-200" },
};

export default function BetriebskostenDetail() {
  const { id } = useParams();
  const [period, setPeriod] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [property, setProperty] = useState(null);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("positions");

  const load = async () => {
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        api.get(`/bka/periods/${id}`),
        api.get(`/bka/catalog`),
      ]);
      setPeriod(p);
      setCatalog(c);
      const { data: prop } = await api.get(`/properties/${p.property_id}`);
      setProperty(prop);
      setPropertyUnits(prop?.units || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loading || !period) {
    return <div className="text-sm text-slate-500 py-10 text-center">Lade Abrechnung…</div>;
  }

  const isDraft = period.status === "draft";
  const status = STATUS_META[period.status] || STATUS_META.draft;
  const totalCost = period.positions_total || 0;
  const missingReceipts = period.positions_missing_receipts?.length || 0;

  const lock = async () => {
    try {
      await api.post(`/bka/periods/${id}/lock`);
      toast.success("Abrechnung abgeschlossen");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Fehler beim Abschließen");
    }
  };
  const reopen = async () => {
    if (!confirm("Wirklich wieder öffnen? Die abgeschlossenen Einzelabrechnungen bleiben, können aber nicht mehr geändert werden, bis erneut abgeschlossen wird.")) return;
    try {
      await api.post(`/bka/periods/${id}/reopen`);
      toast.success("Abrechnung wieder geöffnet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4" data-testid="bka-detail-page">
      {/* Kopf */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/betriebskosten" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-1">
            <ArrowLeft size={12} /> Zurück zur Übersicht
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Receipt size={22} className="text-emerald-700" />
            {period.label || "Betriebskostenabrechnung"}
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{property?.name}</span>
            {property?.property_number && <span>· {property.property_number}</span>}
            <span>·</span>
            <span className="tabular-nums">{formatDate(period.period_from)} – {formatDate(period.period_to)}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${status.cls}`}>
              {period.status === "locked" && <LockKey size={10} />}
              {status.label}
            </span>
            {period.mode === "seg_import" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">SEG-Import</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PeriodMasterDataDialog period={period} catalog={catalog} onSaved={load} />
          {isDraft ? (
            <Button
              onClick={lock} disabled={!period.can_lock}
              data-testid="bka-lock-btn"
              className={period.can_lock ? "bg-emerald-700 hover:bg-emerald-800 gap-1.5" : "gap-1.5"}
              title={period.can_lock ? "Abschließen" : `Erst wenn alle Positionen belegt sind (${missingReceipts} fehlend)`}
            >
              <LockKey size={14} /> Abschließen
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={reopen} data-testid="bka-reopen-btn" className="gap-1.5">
                <LockKeyOpen size={14} /> Wieder öffnen
              </Button>
              <DispatchButton period={period} onReload={load} />
              <NotifyOwnersButton period={period} />
              <BatchVzAdjustButton period={period} onReload={load} />
              <CorrectionButton period={period} onReload={load} />
            </>
          )}
          {tankFuelOf(property) && <OilHeatingButton period={period} fuel={tankFuelOf(property)} onReload={load} />}
          <HeatingAdjustButton period={period} property={property} onReload={load} />
          <OwnerReportButtons period={period} />
        </div>
      </div>

      {/* KPI-Zeile */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Kostensumme" value={formatEUR(totalCost)} />
        <Kpi label="Positionen" value={period.positions?.length ?? 0} />
        <Kpi
          label="Belegquote"
          value={period.positions?.length
            ? `${period.positions.filter((p) => p.receipt_ok).length}/${period.positions.length}`
            : "—"}
          warn={missingReceipts > 0}
        />
        <Kpi label="Belege gesamt" value={period.receipts?.length ?? 0} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="positions" data-testid="tab-positions">Positionen</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Belege ({period.receipts?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements">Einzelabrechnungen</TabsTrigger>
          {period.mode === "seg_import" && (
            <TabsTrigger value="seg-import" data-testid="tab-seg-import">SEG-Import-Assistent</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="positions" className="mt-4">
          <PositionsTab period={period} catalog={catalog} propertyUnits={propertyUnits} onReload={load} />
        </TabsContent>
        <TabsContent value="receipts" className="mt-4">
          <ReceiptsTab period={period} onReload={load} />
        </TabsContent>
        <TabsContent value="settlements" className="mt-4">
          <SettlementsTab period={period} onReload={load} />
        </TabsContent>
        {period.mode === "seg_import" && (
          <TabsContent value="seg-import" className="mt-4">
            <SegImportTab period={period} property={property} catalog={catalog} onReload={load} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, warn }) {
  return (
    <div className={`rounded-lg border ${warn ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums mt-0.5 ${warn ? "text-amber-900" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

// ============================================================================
// Positions-Tab
// ============================================================================
const UMLAGE_LABEL = {
  voll:       { label: "Voll umlagefähig", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  teilweise:  { label: "Teilweise",        cls: "bg-amber-100 text-amber-800 border-amber-200" },
  nicht:      { label: "Nicht umlagefähig", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};
const DIST_LABEL = {
  living_area: "Wohnfläche",
  mea: "MEA",
  persons: "Personen",
  units: "WE",
  consumption: "Verbrauch",
  fixed_share: "Fester Anteil",
};

// iter153 — Kurz-Bezeichnung passend zum PDF für die neue Verteilungs-Tabelle.
const DIST_UNIT_LABEL = {
  living_area: "m²",
  mea: "MEA",
  persons: "Personen",
  units: "Einheiten",
  consumption: "Verbrauch",
  fixed_share: "fester Anteil",
  custom_position: "individuell",
};

// iter153 — Formatier-Helper für die neuen Spalten „Schlüssel ges." und
// „Ihr Umlageschlüssel". Passt die Dezimalstellen an den Schlüssel-Typ an.
function fmtNum(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function fmtKeyTotal(key, value) {
  if (value === null || value === undefined) return "—";
  if (key === "fixed_share") return "100 %";
  if (key === "persons" || key === "units") return String(Math.round(value));
  if (key === "mea") return fmtNum(value, 0);
  return fmtNum(value, 2);
}
function fmtKeyShare(key, value) {
  if (value === null || value === undefined) return "—";
  if (key === "fixed_share") return "100 %";
  if (key === "persons" || key === "units") return String(Math.round(value));
  if (key === "mea") return fmtNum(value, 0);
  return fmtNum(value, 2);
}

function PositionsTab({ period, catalog, propertyUnits, onReload }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedPos, setExpandedPos] = useState(null);  // 2026-02 (iter122)
  // iter169 · Zwischensummen ausklappbar (per Default zugeklappt)
  const [subtotalsOpen, setSubtotalsOpen] = useState({});
  const [preview, setPreview] = useState(null);           // 2026-02 (iter122)
  // 2026-02 (iter131) — neue Workflow-Dialoge
  const [batchOpen, setBatchOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOverPosId, setDragOverPosId] = useState(null);
  const [dropBusyPosId, setDropBusyPosId] = useState(null);
  const isDraft = period.status === "draft";

  // 2026-02 (iter131) — Vorjahres-Positionen kopieren
  const importPrevious = async () => {
    if (!confirm("Positionen aus der letzten Abrechnungsperiode übernehmen? " +
                 "Beträge werden auf 0 gesetzt, bereits vorhandene Kategorien werden übersprungen.")) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/bka/periods/${period.id}/import-previous-positions`);
      toast.success(`${data.copied} Position${data.copied !== 1 ? "en" : ""} übernommen aus „${data.source_period_label || "Vorperiode"}"` +
                    (data.skipped > 0 ? ` · ${data.skipped} übersprungen (bereits vorhanden)` : ""));
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Keine Vorperiode gefunden");
    } finally {
      setImporting(false);
    }
  };

  // 2026-02 (iter131) — Drag & Drop: Belege direkt auf Position hochladen + verknüpfen
  const handleDrop = async (e, position) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPosId(null);
    if (!isDraft) return;
    const files = Array.from(e.dataTransfer.files || []).filter((f) => {
      const t = (f.type || "").toLowerCase();
      return t.startsWith("application/pdf") || t.startsWith("image/");
    });
    if (files.length === 0) {
      toast.error("Bitte PDF- oder Bild-Dateien ablegen");
      return;
    }
    setDropBusyPosId(position.id);
    let ok = 0, err = 0;
    const linkAmount = parseFloat(position.amount_gross || 0) || 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (linkAmount > 0) fd.append("total_gross", String(linkAmount));
        const { data: receipt } = await api.post(
          `/bka/periods/${period.id}/receipts`, fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (linkAmount > 0) {
          await api.post(`/bka/allocations`, {
            receipt_id: receipt.id, position_id: position.id, amount: linkAmount,
          });
        }
        ok += 1;
      } catch (ex) {
        err += 1;
        console.error("drop upload failed", ex);
      }
    }
    setDropBusyPosId(null);
    if (ok > 0) {
      toast.success(`${ok} Beleg${ok !== 1 ? "e" : ""} hochgeladen und verknüpft` +
                    (err > 0 ? ` · ${err} fehlgeschlagen` : ""));
      onReload();
    } else {
      toast.error("Upload fehlgeschlagen");
    }
  };

  // 2026-02 (iter122) — Live-Vorschau der Umlageschlüssel-Verteilung.
  // Zeigt pro Einheit + Position den errechneten Anteilsbetrag, damit sichtbar
  // ist, wie sich die Kosten (MEA/qm/Personen) auf die Einheiten verteilen —
  // OHNE dass „Berechnen" geklickt werden muss.
  useEffect(() => {
    if (!period?.id) return;
    let cancelled = false;
    api.get(`/bka/periods/${period.id}/distribution-preview`)
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [period?.id, period?.positions?.length,
      // Neuberechnen wenn sich ein Positionsbetrag ändert
      (period?.positions || []).map((p) => p.amount_gross).join(",")]);

  const del = async (pid) => {
    if (!confirm("Position wirklich löschen?")) return;
    try {
      await api.delete(`/bka/positions/${pid}`);
      toast.success("Position gelöscht");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // iter132 — Drag & Drop Reordering (intern via MIME "application/x-bka-pos")
  const [dragPosId, setDragPosId] = useState(null);
  const [dragTargetPosId, setDragTargetPosId] = useState(null);
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const isPosDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("application/x-bka-pos");

  const reorderWithinGroup = async (groupPositions, fromId, toId) => {
    if (fromId === toId) return;
    const ids = groupPositions.map((p) => p.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    // Baue Gesamt-Reihenfolge: Gruppe an neuer Stelle einordnen,
    // andere Positionen (andere Gruppen) hinten anhängen mit ihrer alten Reihenfolge.
    const allInGroup = new Set(groupPositions.map((p) => p.id));
    const others = (period.positions || []).filter((p) => !allInGroup.has(p.id)).map((p) => p.id);
    const newOrder = [...ids, ...others];
    try {
      await api.post(`/bka/periods/${period.id}/positions/reorder`, { position_ids: newOrder });
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reihenfolge konnte nicht gespeichert werden");
    }
  };

  const groups = useMemo(() => {
    const g = { voll: [], teilweise: [], nicht: [] };
    (period.positions || []).forEach((p) => (g[p.umlagefaehig] || g.voll).push(p));
    return g;
  }, [period.positions]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-xs text-slate-500 flex-1 min-w-0">
          Kostenpositionen nach BetrKV § 2. Jede Position benötigt für den Abschluss mindestens einen
          Beleg, dessen Zuordnungen den Positionsbetrag exakt abdecken.
          {isDraft && (
            <div className="mt-1 text-[11px] text-slate-400">
              Tipp: Belege lassen sich per Drag & Drop direkt auf eine Position ziehen (mehrere gleichzeitig möglich).
            </div>
          )}
        </div>
        {isDraft && (
          <div className="flex gap-1.5 flex-wrap justify-end">
            <Button size="sm" variant="outline" onClick={importPrevious}
                    disabled={importing} className="gap-1.5"
                    data-testid="pos-import-previous-btn">
              <Copy size={14} /> {importing ? "Übernehme…" : "Vorjahr übernehmen"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBatchOpen(true)}
                    className="gap-1.5" data-testid="pos-batch-btn">
              <ListChecks size={14} /> Mehrere aus Katalog
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => setEditing(null)} data-testid="pos-add-btn" className="gap-1.5">
                  <Plus size={14} /> Position hinzufügen
                </Button>
              </DialogTrigger>
              {/* 2026-02 — Bug-Fix: Formular-State beim Öffnen sauber zurücksetzen.
                  Ohne key würde React die Komponente wiederverwenden und die alten
                  State-Werte (Kategorie, Betrag, …) blieben stehen. */}
              {dialogOpen && (
                <PositionDialog
                  key={editing?.id || "new"}
                  period={period} catalog={catalog} editing={editing}
                  propertyUnits={propertyUnits}
                  onSaved={() => { setDialogOpen(false); setEditing(null); onReload(); }}
                />
              )}
            </Dialog>
          </div>
        )}
      </div>

      {batchOpen && (
        <BatchPositionsDialog
          period={period} catalog={catalog}
          onClose={() => setBatchOpen(false)}
          onSaved={() => { setBatchOpen(false); onReload(); }}
        />
      )}

      {(period.positions || []).length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
          Noch keine Positionen. Füge Kostenpositionen aus dem BetrKV § 2 Katalog hinzu.
        </div>
      )}

      {["voll", "teilweise", "nicht"].map((k) => (
        groups[k].length > 0 && (
          <div key={k} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className={`px-3 py-2 border-b text-xs font-medium flex items-center justify-between ${UMLAGE_LABEL[k].cls.replace("100", "50")}`}>
              <span>{UMLAGE_LABEL[k].label}</span>
              <span className="tabular-nums">
                Σ {formatEUR(groups[k].reduce((s, p) => s + (p.amount_gross || 0), 0))}
              </span>
            </div>
            {/* iter150 — Zwischensummen pro Kategorie (nur wenn mehrere Kategorien in dieser Umlage-Gruppe) */}
            {/* iter169 — ausklappbar (per Default zugeklappt) */}
            {(() => {
              const byCat = {};
              groups[k].forEach((p) => {
                const label = p.category_label || p.category_key || "—";
                if (!byCat[label]) byCat[label] = { total: 0, count: 0 };
                byCat[label].total += p.amount_gross || 0;
                byCat[label].count += 1;
              });
              const entries = Object.entries(byCat).sort((a, b) => b[1].total - a[1].total);
              if (entries.length < 2) return null;
              const isOpen = subtotalsOpen[k];
              return (
                <div className="border-b border-slate-100 bg-slate-50/50" data-testid={`umlage-subtotals-${k}`}>
                  <button
                    type="button"
                    onClick={() => setSubtotalsOpen((s) => ({ ...s, [k]: !s[k] }))}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-100 transition"
                    data-testid={`umlage-subtotals-toggle-${k}`}
                  >
                    <CaretRight size={10} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <span className="font-medium">Zwischensummen ({entries.length}) — {isOpen ? "verbergen" : "anzeigen"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-2 text-[11px] flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-700">
                      {entries.map(([lab, agg]) => (
                        <span key={lab} className="inline-flex items-center gap-1">
                          <span className="text-slate-600">{lab}{agg.count > 1 ? ` (${agg.count})` : ""}:</span>
                          <span className="tabular-nums font-medium">{formatEUR(agg.total)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Kategorie</th>
                  <th className="text-right px-3 py-1.5 font-medium w-24">Betrag</th>
                  <th className="text-left px-3 py-1.5 font-medium w-28">Schlüssel</th>
                  <th className="text-left px-3 py-1.5 font-medium w-40">Belege</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups[k].map((p) => {
                  const isExpanded = expandedPos === p.id;
                  const posInPreview = (preview?.units || []).map((u) => {
                    const row = (u.positions || []).find((pp) => pp.position_id === p.id);
                    return row ? { unit_id: u.unit_id, unit_number: u.unit_number, mea_share: u.mea_share,
                                   living_area: u.living_area, amount: row.unit_share, key_share: row.key_share,
                                   key_total: row.key_total,
                                   // iter153 — Positions-Gesamtbetrag für die neue Spalte „Ausgaben ges."
                                   position_total: row.position_total ?? p.amount_gross } : null;
                  }).filter(Boolean);
                  return (
                  <Fragment key={p.id}>
                  <tr data-testid={`pos-row-${p.id}`}
                      onDragOver={isDraft ? (e) => {
                        if (isFileDrag(e)) {
                          e.preventDefault(); e.dataTransfer.dropEffect = "copy";
                          setDragOverPosId(p.id);
                        } else if (isPosDrag(e)) {
                          e.preventDefault(); e.dataTransfer.dropEffect = "move";
                          setDragTargetPosId(p.id);
                        }
                      } : undefined}
                      onDragLeave={isDraft ? () => {
                        setDragOverPosId((cur) => cur === p.id ? null : cur);
                        setDragTargetPosId((cur) => cur === p.id ? null : cur);
                      } : undefined}
                      onDrop={isDraft ? (e) => {
                        if (isFileDrag(e)) {
                          handleDrop(e, p);
                        } else if (isPosDrag(e)) {
                          e.preventDefault();
                          const fromId = e.dataTransfer.getData("application/x-bka-pos");
                          setDragTargetPosId(null); setDragPosId(null);
                          reorderWithinGroup(groups[k], fromId, p.id);
                        }
                      } : undefined}
                      className={
                        dragOverPosId === p.id ? "bg-sky-100 outline outline-2 outline-sky-400" :
                        dragTargetPosId === p.id ? "border-t-2 border-t-sky-500" :
                        dropBusyPosId === p.id ? "bg-sky-50" :
                        (!p.receipt_ok && isDraft) ? "bg-rose-50/40" :
                        dragPosId === p.id ? "opacity-40" : ""
                      }>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {isDraft && (
                          <span
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("application/x-bka-pos", p.id);
                              e.dataTransfer.effectAllowed = "move";
                              setDragPosId(p.id);
                            }}
                            onDragEnd={() => { setDragPosId(null); setDragTargetPosId(null); }}
                            className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600"
                            title="Ziehen, um die Reihenfolge innerhalb der Gruppe zu ändern"
                            data-testid={`pos-drag-${p.id}`}
                          >
                            <DotsSixVertical size={14} />
                          </span>
                        )}
                        {posInPreview.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedPos(isExpanded ? null : p.id)}
                            className="text-slate-400 hover:text-slate-700"
                            data-testid={`pos-expand-${p.id}`}
                            title={isExpanded ? "Verteilung schließen" : "Verteilung auf Einheiten anzeigen"}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </button>
                        )}
                        <div className="font-medium text-sm">{p.category_label || p.category_key}</div>
                        <PosBadges p={p} />
                        {dropBusyPosId === p.id && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 font-medium">
                            <CloudArrowUp size={11} className="animate-pulse" /> lade Beleg …
                          </span>
                        )}
                      </div>
                      {p.notes && <div className="text-[10px] text-slate-500 mt-0.5 ml-5">{p.notes}</div>}
                      {(p.position_from || p.position_to) && (
                        <div className="text-[10px] text-slate-500 mt-0.5 ml-5">
                          Zeitraum: {p.position_from ? formatDate(p.position_from) : formatDate(period.period_from)} – {p.position_to ? formatDate(p.position_to) : formatDate(period.period_to)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(p.amount_gross)}</td>
                    <td className="px-3 py-2 text-xs">{DIST_LABEL[p.distribution_key] || p.distribution_key}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.receipt_ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle size={11} /> {p.receipt_count} Beleg{p.receipt_count > 1 ? "e" : ""} ({formatEUR(p.receipt_sum)})
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-700">
                          <Warning size={11} weight="fill" /> {formatEUR(p.receipt_sum)} von {formatEUR(p.amount_gross)} belegt
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isDraft && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                  onClick={() => { setEditing(p); setDialogOpen(true); }}
                                  data-testid={`pos-edit-${p.id}`}>
                            ✎
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700"
                                  onClick={() => del(p.id)} data-testid={`pos-del-${p.id}`}>
                            <Trash size={12} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && posInPreview.length > 0 && (
                    <tr className="bg-slate-50">
                      <td colSpan={5} className="px-6 py-3">
                        <div className="text-[11px] font-semibold text-slate-700 mb-2">
                          Verteilung nach {DIST_LABEL[p.distribution_key] || p.distribution_key} ({posInPreview.length} Einheit{posInPreview.length > 1 ? "en" : ""})
                        </div>
                        <table className="w-full text-xs">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="text-left py-1 font-normal">Einheit</th>
                              <th className="text-right py-1 font-normal">Ausgaben ges.</th>
                              <th className="text-right py-1 font-normal">Schlüssel ges.</th>
                              <th className="text-left py-1 font-normal">Schlüssel-Bez.</th>
                              <th className="text-right py-1 font-normal">Ihr Umlageschlüssel</th>
                              <th className="text-right py-1 font-normal">Zeitanteil</th>
                              <th className="text-right py-1 font-normal tabular-nums">Ihr Anteil</th>
                            </tr>
                          </thead>
                          <tbody>
                            {posInPreview.map((row) => (
                              <tr key={row.unit_id} className="border-t border-slate-200">
                                <td className="py-1 font-mono">{row.unit_number || row.unit_id.slice(0, 8)}</td>
                                <td className="py-1 text-right tabular-nums text-slate-500">
                                  {formatEUR(row.position_total || 0)}
                                </td>
                                <td className="py-1 text-right tabular-nums text-slate-500">
                                  {fmtKeyTotal(p.distribution_key, row.key_total)}
                                </td>
                                <td className="py-1 text-slate-500">
                                  {DIST_UNIT_LABEL[p.distribution_key] || "—"}
                                </td>
                                <td className="py-1 text-right tabular-nums">
                                  {fmtKeyShare(p.distribution_key, row.key_share)}
                                </td>
                                <td className="py-1 text-right tabular-nums text-slate-500">—</td>
                                <td className="py-1 text-right tabular-nums font-semibold text-slate-800">{formatEUR(row.amount)}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-slate-300 font-semibold">
                              <td className="py-1" colSpan={6}>Summe</td>
                              <td className="py-1 text-right tabular-nums">{formatEUR(posInPreview.reduce((s, r) => s + r.amount, 0))}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
                })}
              </tbody>
            </table>
          </div>
        )
      ))}
    </div>
  );
}

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");
const segLabel = (sg) => [sg.label, sg.date_from || sg.date_to ? `${fmtD(sg.date_from)}–${fmtD(sg.date_to)}` : ""].filter(Boolean).join(" ") || "Einzelposten";
const TAX_OPTS = [["none", "ohne Steuer"], ["vat", "Umsatzsteuer"], ["insurance_tax", "Versicherungsteuer"]];
const TAX_SHORT = { vat: "USt", insurance_tax: "VersSt", mixed: "Steuer gemischt" };
// Versicherungsteuer (§§ 5, 6 VersStG): Steuersatz × Bemessungsanteil = effektiver Satz auf das Nettoentgelt
const INS_KINDS = [
  ["16.34", "Wohngebäudeversicherung (19 % auf 86 % = 16,34 %)"],
  ["13.2", "Feuerversicherung (22 % auf 60 % = 13,2 %)"],
  ["16.15", "Hausratversicherung (19 % auf 85 % = 16,15 %)"],
  ["19", "Haftpflicht, Glas, Elementar, sonstige (19 %)"],
];

function OilHeatingButton({ period, fuel, onReload }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="oil-heating-btn">{fuel === "lpg" ? "Gastank" : "Ölheizung"}</Button>
      {open && <OilHeatingDialog period={period} fuel={fuel} open={open} onOpenChange={setOpen} onReload={onReload} />}
    </>
  );
}

function OwnerReportButtons({ period }) {
  const [busy, setBusy] = useState("");
  const dl = async (fmt) => {
    setBusy(fmt);
    const w = fmt === "pdf" ? window.open("", "_blank") : null;
    try {
      const { data } = await api.get(`/bka/periods/${period.id}/owner-report`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (fmt === "pdf") { if (w) w.location.href = url; else window.open(url, "_blank"); }
      else { const a = document.createElement("a"); a.href = url; a.download = `Eigentuemer-Auswertung_${(period.label || "BKA").replace(/\s+/g, "_")}.xlsx`; a.click(); }
    } catch { w?.close(); toast.error("Auswertung konnte nicht erstellt werden"); }
    setBusy("");
  };
  return (
    <span className="inline-flex gap-1" data-testid="owner-report-buttons">
      <Button variant="outline" className="gap-1.5" disabled={!!busy} onClick={() => dl("pdf")} data-testid="owner-report-pdf"><FilePdf size={14} /> Eigentümer-Auswertung</Button>
      <Button variant="outline" disabled={!!busy} onClick={() => dl("xlsx")} data-testid="owner-report-xlsx" title="Excel-Export">Excel</Button>
    </span>
  );
}

function PosBadges({ p }) {
  const segs = p.segments || [];
  return (
    <span className="inline-flex flex-wrap gap-1 ml-1" data-testid={`pos-badges-${p.id}`}>
      {(p.owner_only || String(p.category_key || "").startsWith("owner_")) && <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-800" title="Nur in der Eigentümer-Auswertung, nicht auf Mieterabrechnungen">Eigentümerkosten</span>}
      {segs.length > 0 && <span className="text-[10px] px-1.5 rounded bg-sky-100 text-sky-800" title={segs.map((sg) => `${segLabel(sg)}: ${formatEUR(sg.amount_gross)}${sg.receipt_sum ? ` (Belege ${formatEUR(sg.receipt_sum)})` : ""}`).join("\n")}>{segs.length} {segs.some((sg) => sg.date_from || sg.date_to) ? "Teilzeiträume" : "Einzelposten"}</span>}
      {p.tax_type && p.tax_type !== "none" && <span className="text-[10px] px-1.5 rounded bg-slate-100 text-slate-700">{TAX_SHORT[p.tax_type]} {formatEUR(p.tax_amount)}</span>}
    </span>
  );
}

function SegmentsEditor({ segments, setSegments, defaultTax }) {
  const upd = (i, k, v) => setSegments(segments.map((sg, j) => (j === i ? { ...sg, [k]: v } : sg)));
  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded p-2 space-y-1.5" data-testid="pos-segments">
      <div className="text-[11px] text-sky-900">Je Zeile ein Teilzeitraum (z. B. nach Preiserhöhung) oder ein Einzelposten (z. B. Kontoführung, Mahnkosten). Zeiträume werden zeitanteilig umgelegt; Belege lassen sich je Zeile zuordnen.</div>
      {segments.map((sg, i) => (
        <div key={sg.id || i} className="grid grid-cols-12 gap-1 items-center rounded bg-white/70 p-1" data-testid={`pos-seg-${i}`}>
          <Input className="h-7 text-xs col-span-5" placeholder="Bezeichnung (z. B. bis Preiserhöhung)" value={sg.label || ""} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`pos-seg-label-${i}`} />
          <Input type="number" step="0.01" className="h-7 text-xs col-span-3 text-right" placeholder="Brutto €" value={sg.amount_gross ?? ""} onChange={(e) => upd(i, "amount_gross", e.target.value)} data-testid={`pos-seg-amount-${i}`} />
          <select className="h-7 text-[11px] col-span-3 border rounded bg-white px-1" value={sg.tax_type || defaultTax.type} onChange={(e) => upd(i, "tax_type", e.target.value)} data-testid={`pos-seg-tax-${i}`}>
            {TAX_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <button type="button" className="text-rose-600 text-xs col-span-1" onClick={() => setSegments(segments.filter((_, j) => j !== i))} data-testid={`pos-seg-remove-${i}`}>✕</button>
          <span className="col-span-1 text-[10px] text-slate-500 text-right">von</span>
          <Input type="date" lang="de-DE" className="h-7 text-xs col-span-5" value={sg.date_from || ""} onChange={(e) => upd(i, "date_from", e.target.value)} data-testid={`pos-seg-from-${i}`} />
          <span className="col-span-1 text-[10px] text-slate-500 text-right">bis</span>
          <Input type="date" lang="de-DE" className="h-7 text-xs col-span-5" value={sg.date_to || ""} onChange={(e) => upd(i, "date_to", e.target.value)} data-testid={`pos-seg-to-${i}`} />
        </div>))}
      <div className="flex justify-between items-center">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSegments([...segments, { label: "", date_from: "", date_to: "", amount_gross: "" }])} data-testid="pos-seg-add">+ Zeile</Button>
        <span className="text-xs font-medium tabular-nums" data-testid="pos-seg-sum">Summe {formatEUR(segments.reduce((a, sg) => a + (parseFloat(sg.amount_gross) || 0), 0))}</span>
      </div>
    </div>
  );
}

function PositionDialog({ period, catalog, editing, onSaved, propertyUnits }) {
  const isEdit = !!editing;
  const [categoryKey, setCategoryKey] = useState(editing?.category_key || "");
  const [customLabel, setCustomLabel] = useState(editing?.category_label || "");
  const [amount, setAmount] = useState(editing?.amount_gross ?? "");
  const [umlagefaehig, setUmlagefaehig] = useState(editing?.umlagefaehig || "voll");
  const [percent, setPercent] = useState(editing?.umlagefaehig_percent ?? "");
  const [key, setKey] = useState(editing?.distribution_key || "living_area");
  const [posFrom, setPosFrom] = useState(editing?.position_from?.slice(0, 10) || "");
  const [posTo, setPosTo] = useState(editing?.position_to?.slice(0, 10) || "");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [customShares, setCustomShares] = useState(editing?.custom_shares || {});
  // iter133 — Per-Einheit-Verbrauchswerte manuell erfassbar (Heizung, Wasser, ...)
  const [consumptionValues, setConsumptionValues] = useState(editing?.consumption_values || {});
  const [consumptionUnit, setConsumptionUnit] = useState(editing?.consumption_unit || "");
  // iter148 — Optionaler Gesamt-Bezugswert (z. B. Hauptzähler / Messdienst-Gesamt)
  const [totalKeyValue, setTotalKeyValue] = useState(editing?.total_key_value ?? "");
  // iter149 — Bei „Fester Anteil": Ziel-Einheit (Position gilt nur für diese)
  const [fixedShareUnitId, setFixedShareUnitId] = useState(editing?.fixed_share_unit_id || "");
  const [saving, setSaving] = useState(false);
  const [segments, setSegments] = useState((editing?.segments || []).map((sg) => ({ ...sg, date_from: sg.date_from || "", date_to: sg.date_to || "" })));
  const [taxType, setTaxType] = useState(editing?.tax_type && editing.tax_type !== "mixed" ? editing.tax_type : "none");
  const [taxRate, setTaxRate] = useState(editing?.tax_rate ?? 19);
  const [taxAmount, setTaxAmount] = useState(editing?.tax_amount_manual && !(editing?.segments || []).length ? String(editing.tax_amount ?? "") : "");
  const useSegs = segments.length > 0;
  const segSum = segments.reduce((a, sg) => a + (parseFloat(sg.amount_gross) || 0), 0);
  const isOwnerCat = String(categoryKey || "").startsWith("owner_");
  useEffect(() => {
    if (!editing && categoryKey === "11_versicherung_sach" && taxType === "none") { setTaxType("insurance_tax"); setTaxRate(16.34); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  const allCats = [...(catalog?.betrkv || []), ...(catalog?.weg_extra || []), ...(catalog?.owner_extra || [])];
  const distKeys = catalog?.distribution_keys || [];
  const propertyCustomKeys = catalog?.property_custom_keys || [];

  // iter150 — Vorjahres-Vergleich für die gewählte Kategorie
  const [prevYear, setPrevYear] = useState(null);
  useEffect(() => {
    if (!categoryKey || !period?.id) { setPrevYear(null); return; }
    api.get(`/bka/periods/${period.id}/prior-year`, { params: { category_key: categoryKey } })
      .then(({ data }) => {
        const first = (data?.positions || [])[0];
        setPrevYear(first ? { ...first, prev_label: data?.prev_period?.label } : null);
      })
      .catch(() => setPrevYear(null));
  }, [categoryKey, period?.id]);

  const setFromCatalog = (v) => {
    setCategoryKey(v);
    const cat = allCats.find((c) => c.key === v);
    if (cat) {
      if (!isEdit) {
        setUmlagefaehig(cat.default_umlagefaehig);
        setKey(cat.default_key);
      }
    }
  };

  const save = async () => {
    if (!categoryKey || (!useSegs && !amount)) return;
    setSaving(true);
    try {
      const payload = {
        period_id: period.id,
        category_key: categoryKey,
        category_label: customLabel || undefined,
        amount_gross: useSegs ? Math.round(segSum * 100) / 100 : parseFloat(amount),
        segments: useSegs ? segments.map((sg) => ({ ...sg, amount_gross: parseFloat(sg.amount_gross) || 0, date_from: sg.date_from || null, date_to: sg.date_to || null,
          tax_type: sg.tax_type || taxType, tax_rate: (sg.tax_type || taxType) === "none" ? null : parseFloat(sg.tax_rate ?? taxRate), tax_amount: null })) : [],
        tax_type: taxType,
        tax_rate: taxType === "none" ? null : parseFloat(taxRate),
        tax_amount: !useSegs && taxType !== "none" && taxAmount !== "" ? parseFloat(String(taxAmount).replace(",", ".")) : null,
        umlagefaehig: isOwnerCat ? "nicht" : umlagefaehig,
        umlagefaehig_percent: umlagefaehig === "teilweise" ? parseFloat(percent || 100) : null,
        distribution_key: key,
        position_from: posFrom || null,
        position_to: posTo || null,
        notes: notes || null,
        custom_shares: key === "custom_position" ? customShares : null,
        // iter133 — Verbrauchswerte je Einheit (aus Eingabefeldern, falls consumption)
        consumption_values: key === "consumption" ? (
          Object.keys(consumptionValues).length > 0
            ? Object.fromEntries(
                Object.entries(consumptionValues)
                  .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                  .map(([k2, v]) => [k2, parseFloat(v) || 0])
              )
            : null
        ) : null,
        consumption_unit: key === "consumption" ? (consumptionUnit || null) : null,
        // iter148 — Gesamt-Bezugswert (Messdienst-Gesamt): wenn leer → null (System nutzt Summe der Einheits-Werte)
        total_key_value: key === "consumption" && totalKeyValue !== "" && totalKeyValue !== null
          ? parseFloat(totalKeyValue) : null,
        // iter149 — Fester Anteil: Ziel-Einheit
        fixed_share_unit_id: key === "fixed_share" ? (fixedShareUnitId || null) : null,
      };
      if (isEdit) {
        await api.patch(`/bka/positions/${editing.id}`, payload);
      } else {
        await api.post(`/bka/periods/${period.id}/positions`, payload);
      }
      toast.success(isEdit ? "Aktualisiert" : "Position hinzugefügt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-xl" data-testid="pos-dialog">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Position bearbeiten" : "Neue Position"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Kategorie (BetrKV § 2 oder WEG-Zusatz)</Label>
          <Select value={categoryKey} onValueChange={setFromCatalog}>
            <SelectTrigger data-testid="pos-category"><SelectValue placeholder="Wählen" /></SelectTrigger>
            <SelectContent className="max-h-96">
              <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold">BetrKV § 2</div>
              {(catalog?.betrkv || []).map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
              <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold border-t mt-1">WEG-Zusatz (nicht umlagefähig)</div>
              {(catalog?.weg_extra || []).map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
              <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold border-t mt-1">Eigentümerkosten (nur Eigentümer-Auswertung)</div>
              {(catalog?.owner_extra || []).map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
              <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold border-t mt-1">Frei</div>
              <SelectItem value="_custom_">Eigene Bezeichnung …</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Freitext-Titel: überschreibt category_label */}
        <div>
          <Label>Eigene Bezeichnung {categoryKey && categoryKey !== "_custom_" ? "(optional, überschreibt Kategorie-Titel)" : "(erforderlich)"}</Label>
          <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
                 placeholder={categoryKey === "_custom_" ? "z. B. Wartung Rauchmelder" : "z. B. Müllabfuhr Q1-Q2"}
                 data-testid="pos-custom-label" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Bruttobetrag (EUR){useSegs ? " – Summe der Zeilen" : ""}</Label>
            <Input type="number" step="0.01" value={useSegs ? segSum.toFixed(2) : amount} disabled={useSegs} onChange={(e) => setAmount(e.target.value)} data-testid="pos-amount" />
            {prevYear && prevYear.amount_gross ? (() => {
              const prev = Number(prevYear.amount_gross || 0);
              const cur = Number(amount || 0);
              const delta = cur - prev;
              const pct = prev > 0 ? (delta / prev) * 100 : 0;
              const cls = Math.abs(pct) < 5 ? "text-slate-600" : pct > 15 ? "text-rose-700" : pct < -15 ? "text-emerald-700" : "text-amber-700";
              return (
                <div className={`text-[11px] mt-1 ${cls}`} data-testid="pos-prev-year">
                  Vorjahr {prevYear.prev_label ? `(${prevYear.prev_label})` : ""}:{" "}
                  <strong>{prev.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                  {cur > 0 && (
                    <span> · Delta {delta >= 0 ? "+" : ""}{delta.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € ({pct >= 0 ? "+" : ""}{pct.toFixed(1)} %)</span>
                  )}
                </div>
              );
            })() : null}
          </div>
          <div>
            <Label>Umlagefähigkeit</Label>
            <Select value={umlagefaehig} onValueChange={setUmlagefaehig}>
              <SelectTrigger data-testid="pos-umlage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="voll">Voll umlagefähig</SelectItem>
                <SelectItem value="teilweise">Teilweise umlagefähig</SelectItem>
                <SelectItem value="nicht">Nicht umlagefähig</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {isOwnerCat && <div className="text-[11px] rounded border border-amber-200 bg-amber-50 text-amber-900 px-2 py-1" data-testid="pos-owner-hint">Eigentümerkosten: erscheinen nicht auf den Mieterabrechnungen, nur in der Eigentümer-Auswertung (Gesamtkosten, Belege, späterer Bankabgleich).</div>}
        <div className="grid grid-cols-3 gap-2" data-testid="pos-tax-block">
          <div><Label className="text-xs">Steuer im Betrag</Label>
            <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={taxType} onChange={(e) => { setTaxType(e.target.value); setTaxRate(e.target.value === "vat" ? 19 : e.target.value === "insurance_tax" ? 16.34 : ""); }} data-testid="pos-tax-type">
              {TAX_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          {taxType !== "none" && <div><Label className="text-xs">Satz (%)</Label>
            {taxType === "vat"
              ? <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="pos-tax-rate"><option value={19}>19 %</option><option value={7}>7 %</option></select>
              : <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="pos-tax-rate" />}</div>}
          {taxType === "insurance_tax" && <div className="col-span-3"><Label className="text-xs">Versicherungsart</Label>
            <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={INS_KINDS.some(([v]) => parseFloat(v) === parseFloat(taxRate)) ? String(parseFloat(taxRate)) : ""} onChange={(e) => e.target.value && setTaxRate(e.target.value)} data-testid="pos-ins-kind">
              <option value="">— eigener Satz —</option>{INS_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>}
          {taxType !== "none" && !useSegs && <div><Label className="text-xs">Steuerbetrag (optional)</Label>
            <Input value={taxAmount} placeholder={amount ? (parseFloat(amount) * parseFloat(taxRate || 0) / (100 + parseFloat(taxRate || 0))).toFixed(2) : "auto"} onChange={(e) => setTaxAmount(e.target.value)} data-testid="pos-tax-amount" /></div>}
          <p className="col-span-3 text-[10px] text-slate-500 -mt-1">USt wird bei Vorsteuer-Mietern (Mietverhältnis mit USt-Satz) herausgerechnet und auf die Summe neu ausgewiesen. Versicherungsteuer ist nicht abziehbar und bleibt Kostenbestandteil.</p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-xs font-medium"><input type="checkbox" checked={useSegs} onChange={(e) => setSegments(e.target.checked ? [{ label: "", date_from: posFrom || "", date_to: "", amount_gross: amount || "" }] : [])} data-testid="pos-seg-toggle" /> In Teilzeiträume / Einzelposten aufteilen</label>
          {useSegs && <div className="mt-1.5"><SegmentsEditor segments={segments} setSegments={setSegments} defaultTax={{ type: taxType }} /></div>}
        </div>
        {umlagefaehig === "teilweise" && !isOwnerCat && (
          <div>
            <Label>Umlage-Prozentsatz (%)</Label>
            <Input type="number" min="0" max="100" step="1" value={percent}
                   onChange={(e) => setPercent(e.target.value)} data-testid="pos-percent" />
            <p className="text-[10px] text-slate-500 mt-1">
              Nur der gekennzeichnete Prozentsatz wird auf Mieter umgelegt.
            </p>
          </div>
        )}
        <div>
          <Label>Umlageschlüssel</Label>
          <Select value={key} onValueChange={setKey}>
            <SelectTrigger data-testid="pos-key"><SelectValue /></SelectTrigger>
            <SelectContent>
              {distKeys.map((k) => (
                <SelectItem key={k.key} value={k.key}>
                  {k.label}
                  <span className="text-[10px] text-slate-500 ml-1">· {k.note}</span>
                </SelectItem>
              ))}
              {propertyCustomKeys.length > 0 && (
                <>
                  <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold border-t mt-1">Objekt-Schlüssel</div>
                  {propertyCustomKeys.map((k) => (
                    <SelectItem key={`custom:${k.id}`} value={`custom:${k.id}`}>
                      {k.name}
                      <span className="text-[10px] text-slate-500 ml-1">· wiederverwendbar</span>
                    </SelectItem>
                  ))}
                </>
              )}
              <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold border-t mt-1">Ad-hoc</div>
              <SelectItem value="custom_position">Eigene Anteile für DIESE Position …</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {key === "custom_position" && propertyUnits && propertyUnits.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded p-3">
            <div className="text-xs font-semibold text-amber-900 mb-2">
              Individuelle Anteile pro Einheit (Zahl oder %)
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {propertyUnits.map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-xs">
                  <span className="flex-1 truncate text-amber-900">{u.unit_number}</span>
                  <Input type="number" step="0.01" className="h-7 w-24 text-xs"
                         value={customShares[u.id] ?? ""}
                         onChange={(e) => setCustomShares({ ...customShares, [u.id]: e.target.value })}
                         placeholder="0" data-testid={`pos-custom-share-${u.id}`} />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-amber-800 mt-2">
              Die Anteile werden ins Verhältnis gesetzt. Beispiel: Einheit A = 3, B = 1 → A bekommt 75 %, B bekommt 25 %.
              Wer 0 oder leer ist, bekommt nichts.
            </p>
          </div>
        )}
        {/* iter149 — Fester Anteil: Position gilt exakt für eine Einheit */}
        {key === "fixed_share" && propertyUnits && propertyUnits.length > 0 && (
          <div className="border border-violet-200 bg-violet-50 rounded p-3" data-testid="pos-fixed-share-block">
            <div className="text-xs font-semibold text-violet-900 mb-2">
              Fester Anteil — für welche Einheit?
            </div>
            <p className="text-[11px] text-violet-800 mb-2">
              Ein „fester Anteil" gilt IMMER genau für <b>eine</b> Einheit — z. B. eine
              individuelle Reparatur oder ein Sondermerkmal. Wählen Sie die Einheit,
              die 100 % dieser Position trägt. Alle anderen Einheiten erhalten 0 EUR.
            </p>
            <select
              value={fixedShareUnitId}
              onChange={(e) => setFixedShareUnitId(e.target.value)}
              className="w-full h-9 border border-violet-300 rounded px-2 text-sm bg-white"
              data-testid="pos-fixed-share-unit"
            >
              <option value="">— Einheit wählen —</option>
              {propertyUnits.map((u) => (
                <option key={u.id} value={u.id}>{u.unit_number}{u.floor ? ` · ${u.floor}` : ""}</option>
              ))}
            </select>
            {!fixedShareUnitId && (
              <div className="text-[11px] text-rose-700 mt-1 font-medium">
                Ohne Ziel-Einheit landet die Position bei ALLEN Einheiten mit 100 % — bitte auswählen.
              </div>
            )}
          </div>
        )}
        {key === "consumption" && propertyUnits && propertyUnits.length > 0 && (          <div className="border border-sky-200 bg-sky-50 rounded p-3" data-testid="pos-consumption-inputs">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-sky-900">
                Verbrauchswerte pro Einheit (Heizkostenverordnung § 7)
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-[10px] text-sky-900">Einheit</Label>
                <Input value={consumptionUnit}
                       onChange={(e) => setConsumptionUnit(e.target.value)}
                       className="h-6 w-20 text-xs" placeholder="kWh, m³, MWh"
                       data-testid="pos-consumption-unit" />
              </div>
            </div>
            {/* iter148 — Gesamt-Bezugswert (Messdienst / Hauptzähler) */}
            <div className="mb-3 flex items-start gap-2 bg-white/70 rounded border border-sky-100 p-2">
              <div className="flex-1">
                <Label className="text-[11px] text-sky-900 font-semibold">
                  Gesamt-Bezugswert (optional)
                </Label>
                <p className="text-[10px] text-sky-800/80 mt-0.5 leading-tight">
                  Wenn der Messdienst / Hauptzähler einen Gesamtwert liefert, hier eintragen.
                  Berechnung dann: <span className="font-mono">Anteil = Gesamtkosten ÷ Gesamt-Bezugswert × Einheits-Wert</span>.
                  Leer = System verwendet Summe der Einheits-Werte als Divisor (klassisch).
                </p>
              </div>
              <Input type="number" step="0.01" min="0" value={totalKeyValue}
                     onChange={(e) => setTotalKeyValue(e.target.value)}
                     className="h-8 w-32 text-xs" placeholder="z. B. 1000"
                     data-testid="pos-total-key-value" />
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {propertyUnits.map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-xs">
                  <span className="flex-1 truncate text-sky-900">{u.unit_number}</span>
                  <Input type="number" step="0.01" className="h-7 w-24 text-xs"
                         value={consumptionValues[u.id] ?? ""}
                         onChange={(e) => setConsumptionValues({
                           ...consumptionValues, [u.id]: e.target.value,
                         })}
                         placeholder="0" data-testid={`pos-consumption-${u.id}`} />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-sky-800 mt-2">
              <strong>Ohne Gesamt-Bezugswert:</strong> Anteil = Gesamtkosten × (Einheits-Wert ÷ Summe aller Einheits-Werte). <br />
              <strong>Mit Gesamt-Bezugswert (z. B. Messdienst-Ablesung):</strong> Anteil = Gesamtkosten ÷ Gesamt-Bezugswert × Einheits-Wert.
              Verluste / Allgemeinanteile bleiben so unverteilt.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Abweichender Zeitraum <span className="text-slate-500 font-normal">– von (optional)</span></Label>
            <Input type="date" value={posFrom} onChange={(e) => setPosFrom(e.target.value)} data-testid="pos-from" className="h-9" />
          </div>
          <div>
            <Label className="text-xs">Abweichender Zeitraum <span className="text-slate-500 font-normal">– bis (optional)</span></Label>
            <Input type="date" value={posTo} onChange={(e) => setPosTo(e.target.value)} data-testid="pos-to" className="h-9" />
          </div>
        </div>
        <div>
          <Label>Notiz (optional)</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pos-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving || !categoryKey || (useSegs ? segSum <= 0 : !amount)} data-testid="pos-save">
          {saving ? "Speichere…" : (isEdit ? "Speichern" : "Hinzufügen")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================================
// Massen-Anlage aus BetrKV-Katalog (iter131)
// ============================================================================
function BatchPositionsDialog({ period, catalog, onClose, onSaved }) {
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Bereits angelegte Kategorien: nicht erneut vorschlagen
  const existingKeys = new Set((period.positions || []).map((p) => p.category_key));

  const groups = [
    { title: "BetrKV § 2 (umlagefähig)", items: catalog?.betrkv || [] },
    { title: "WEG-Zusatz (nicht umlagefähig)", items: catalog?.weg_extra || [] },
  ];
  const q = search.trim().toLowerCase();
  const filtered = groups.map((g) => ({
    ...g,
    items: g.items.filter((c) =>
      !q || (c.label || "").toLowerCase().includes(q) || (c.key || "").toLowerCase().includes(q)
    ),
  }));
  const selectedKeys = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selectedCount = selectedKeys.length;

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const selectAll = (items) => {
    const next = { ...selected };
    items.forEach((c) => { if (!existingKeys.has(c.key)) next[c.key] = true; });
    setSelected(next);
  };
  const clearAll = () => setSelected({});

  const save = async () => {
    if (selectedCount === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/bka/periods/${period.id}/positions/batch`,
        { category_keys: selectedKeys },
      );
      toast.success(`${data.created} Position${data.created !== 1 ? "en" : ""} angelegt` +
                    (data.skipped > 0 ? ` · ${data.skipped} übersprungen` : ""));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="pos-batch-dialog">
        <DialogHeader>
          <DialogTitle>Mehrere Positionen aus Katalog anlegen</DialogTitle>
          <DialogDescription>
            Wähle die gewünschten Kostenarten aus dem BetrKV-§-2-Katalog. Positionen werden mit
            Betrag 0,00 € angelegt — Beträge und Belege trägst du danach in der Liste ein.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Input
            placeholder="Suche (z. B. Wasser, Aufzug, Grundsteuer)…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="pos-batch-search"
            className="flex-1"
          />
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {selectedCount} gewählt
          </div>
          {selectedCount > 0 && (
            <Button size="sm" variant="ghost" onClick={clearAll} className="text-xs h-8">
              Zurücksetzen
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
          {filtered.map((g, gi) => (
            <div key={gi}>
              <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 sticky top-0 text-[11px] font-semibold uppercase text-slate-500">
                <span>{g.title}</span>
                {g.items.length > 0 && (
                  <button
                    type="button"
                    onClick={() => selectAll(g.items)}
                    className="text-[10px] font-normal text-sky-600 hover:text-sky-800"
                    data-testid={`pos-batch-selectall-${gi}`}
                  >
                    Alle im Bereich wählen
                  </button>
                )}
              </div>
              {g.items.length === 0 && (
                <div className="px-3 py-3 text-xs text-slate-400 italic">Keine Treffer</div>
              )}
              {g.items.map((c) => {
                const exists = existingKeys.has(c.key);
                const isChecked = !!selected[c.key];
                return (
                  <label
                    key={c.key}
                    className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${
                      exists ? "opacity-50 cursor-not-allowed" : ""
                    } ${isChecked ? "bg-sky-50" : ""}`}
                    data-testid={`pos-batch-item-${c.key}`}
                  >
                    <Checkbox
                      checked={isChecked}
                      disabled={exists}
                      onCheckedChange={() => !exists && toggle(c.key)}
                    />
                    <span className="flex-1 truncate">{c.label}</span>
                    {exists && <span className="text-[10px] text-slate-500">bereits vorhanden</span>}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving || selectedCount === 0}
                  data-testid="pos-batch-save">
            {saving ? "Lege an…" : `${selectedCount} Position${selectedCount !== 1 ? "en" : ""} anlegen`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Belege-Tab (Multi-Verknüpfung)
// ============================================================================
function ReceiptsTab({ period, onReload }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(null); // receipt für Verknüpfung
  const [editOpen, setEditOpen] = useState(null); // iter169 · receipt für Edit
  const isDraft = period.status === "draft";

  const del = async (rid) => {
    if (!confirm("Beleg wirklich löschen? Verknüpfungen werden entfernt.")) return;
    try {
      await api.delete(`/bka/receipts/${rid}`);
      toast.success("Beleg entfernt");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // iter169 · Sub-Beleg-Verknüpfung (parent_receipt_id setzen/zurücksetzen)
  const setParent = async (rid, parentId) => {
    try {
      await api.patch(`/bka/receipts/${rid}`, { parent_receipt_id: parentId || null });
      toast.success(parentId ? "Als Sub-Beleg verknüpft" : "Sub-Beleg-Verknüpfung entfernt");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Multi-Verknüpfung möglich: Ein Beleg kann mehreren Positionen mit unterschiedlichen
          Teilbeträgen zugeordnet werden (z. B. WEG-Jahresabrechnung).
        </div>
        {isDraft && (
          <div className="flex gap-2">
            <ConsumptionCsvButton periodId={period.id} onReload={onReload} />
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="receipt-upload-btn" className="gap-1.5">
                  <Upload size={14} /> Beleg hochladen
                </Button>
              </DialogTrigger>
              {/* 2026-02 — Bug-Fix: Nur mounten wenn offen, damit alter Upload-State
                  (Datei, Aussteller, Betrag …) nicht in den nächsten Upload leckt. */}
              {uploadOpen && (
                <ReceiptUploadDialog period={period} onSaved={() => { setUploadOpen(false); onReload(); }} />
              )}
            </Dialog>
          </div>
        )}
      </div>
      {(period.receipts || []).length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
          Noch keine Belege. Lade Rechnungen, Abrechnungen und Nachweise hoch — sie können anschließend
          mit Positionen (auch mehreren) verknüpft werden.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Beleg</th>
                <th className="text-left px-3 py-2 font-medium w-32">Aussteller</th>
                <th className="text-left px-3 py-2 font-medium w-28">Datum</th>
                <th className="text-right px-3 py-2 font-medium w-24">Betrag</th>
                <th className="text-right px-3 py-2 font-medium w-24">Zugeordnet</th>
                <th className="w-40"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(period.receipts || []).map((r) => {
                const isSub = !!r.parent_receipt_id;
                const parent = isSub ? (period.receipts || []).find((x) => x.id === r.parent_receipt_id) : null;
                const linkableParents = (period.receipts || []).filter(
                  (x) => x.id !== r.id && !x.parent_receipt_id && (x.sub_receipt_ids || []).length >= 0,
                );
                return (
                <tr key={r.id} data-testid={`receipt-row-${r.id}`}
                    className={isSub ? "bg-indigo-50/30" : ""}>
                  <td className="px-3 py-2">
                    {isSub && <span className="text-[10px] mr-1 text-indigo-700">↳ Sub</span>}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/bka/receipts/${r.id}`}
                      onClick={(e) => { e.preventDefault(); openAuthenticatedPdf(`/bka/receipts/${r.id}`); }}
                      className="text-slate-900 font-medium hover:underline"
                    >{r.filename}</a>
                    {r.doc_number && <div className="text-[10px] text-slate-500">RE-Nr {r.doc_number}</div>}
                    {isSub && parent && (
                      <div className="text-[10px] text-indigo-700 mt-0.5">
                        Enthalten in: <b>{parent.filename}</b>
                      </div>
                    )}
                    {(r.sub_receipt_ids || []).length > 0 && (
                      <div className="text-[10px] text-teal-700 mt-0.5">
                        + {r.sub_receipt_ids.length} Sub-Beleg{r.sub_receipt_ids.length > 1 ? "e" : ""} enthalten
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.supplier || "—"}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{r.doc_date ? formatDate(r.doc_date) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.total_gross != null ? formatEUR(r.total_gross) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">
                    {isSub ? (
                      <span className="text-indigo-700 italic">im Parent</span>
                    ) : (
                      <span className={r.allocated_sum > 0 ? "text-emerald-700 font-medium" : "text-slate-400"}>
                        {formatEUR(r.allocated_sum)}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {isDraft && (
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {!isSub && (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                                  onClick={() => setLinkOpen(r)}
                                  data-testid={`receipt-link-${r.id}`}>
                            Verknüpfen
                          </Button>
                        )}
                        {/* iter169 · Sub-Beleg-Toggle */}
                        {!isSub && linkableParents.length > 0 && (
                          <select
                            className="h-7 border rounded px-1 text-xs bg-white"
                            defaultValue=""
                            onChange={(e) => e.target.value && setParent(r.id, e.target.value)}
                            data-testid={`receipt-parent-${r.id}`}
                          >
                            <option value="">Als Sub-Beleg von …</option>
                            {linkableParents.map((p) => (
                              <option key={p.id} value={p.id}>{p.filename}</option>
                            ))}
                          </select>
                        )}
                        {isSub && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-indigo-700"
                                  onClick={() => setParent(r.id, null)}
                                  data-testid={`receipt-unsub-${r.id}`}>
                            Lösen
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                onClick={() => setEditOpen(r)} data-testid={`receipt-edit-${r.id}`}
                                title="Beleg bearbeiten">
                          <PencilSimple size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600"
                                onClick={() => del(r.id)} data-testid={`receipt-del-${r.id}`}>
                          <Trash size={12} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
      {linkOpen && (
        <ReceiptLinkDialog
          period={period} receipt={linkOpen}
          open={!!linkOpen} onOpenChange={(o) => { if (!o) setLinkOpen(null); }}
          onReload={() => { setLinkOpen(null); onReload(); }}
        />
      )}
      {editOpen && (
        <ReceiptEditDialog
          receipt={editOpen}
          open={!!editOpen} onOpenChange={(o) => { if (!o) setEditOpen(null); }}
          onSaved={() => { setEditOpen(null); onReload(); }}
        />
      )}
    </div>
  );
}

// iter169 · Beleg-Metadaten editieren
function ReceiptEditDialog({ receipt, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    supplier: receipt.supplier || "",
    doc_date: receipt.doc_date || "",
    doc_number: receipt.doc_number || "",
    total_gross: receipt.total_gross ?? "",
    notes: receipt.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/bka/receipts/${receipt.id}`, {
        supplier: form.supplier || null,
        doc_date: form.doc_date || null,
        doc_number: form.doc_number || null,
        total_gross: form.total_gross === "" ? null : Number(form.total_gross),
        notes: form.notes || null,
      });
      toast.success("Beleg aktualisiert");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="receipt-edit-dialog">
        <DialogHeader><DialogTitle>Beleg bearbeiten: {receipt.filename}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Aussteller</Label>
            <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                   data-testid="edit-receipt-supplier" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Datum</Label>
              <Input type="date" value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })}
                     data-testid="edit-receipt-date" />
            </div>
            <div>
              <Label className="text-xs">Beleg-Nr.</Label>
              <Input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })}
                     data-testid="edit-receipt-docnr" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Bruttobetrag (EUR)</Label>
            <Input type="number" step="0.01" value={form.total_gross}
                   onChange={(e) => setForm({ ...form, total_gross: e.target.value })}
                   data-testid="edit-receipt-total" />
          </div>
          <div>
            <Label className="text-xs">Notiz / Beschreibung</Label>
            <textarea
              className="w-full min-h-[80px] rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="edit-receipt-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="edit-receipt-save">
            {busy ? "Speichert…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptUploadDialog({ period, onSaved }) {
  const [file, setFile] = useState(null);
  const [supplier, setSupplier] = useState("");
  const [docDate, setDocDate] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [totalGross, setTotalGross] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    if (!file) { toast.error("Bitte Datei wählen"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (supplier) fd.append("supplier", supplier);
      if (docDate) fd.append("doc_date", docDate);
      if (docNumber) fd.append("doc_number", docNumber);
      if (totalGross) fd.append("total_gross", totalGross);
      if (notes) fd.append("notes", notes);
      await api.post(`/bka/periods/${period.id}/receipts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Beleg hochgeladen");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Upload");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent data-testid="receipt-upload-dialog">
      <DialogHeader>
        <DialogTitle>Beleg hochladen</DialogTitle>
        <DialogDescription>PDF, JPG, PNG oder HEIC — max. 20 MB.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Datei</Label>
          <Input ref={fileRef} type="file" accept="application/pdf,image/*"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="receipt-file" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Aussteller</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)}
                   placeholder="z. B. Stadtwerke" data-testid="receipt-supplier" />
          </div>
          <div>
            <Label>Rechnungsdatum</Label>
            <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)}
                   data-testid="receipt-date" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Rechnungsnummer</Label>
            <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)}
                   data-testid="receipt-number" />
          </div>
          <div>
            <Label>Gesamtbetrag brutto</Label>
            <Input type="number" step="0.01" value={totalGross} onChange={(e) => setTotalGross(e.target.value)}
                   data-testid="receipt-total" />
          </div>
        </div>
        <div>
          <Label>Notiz</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="receipt-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={!file || saving} data-testid="receipt-save">
          {saving ? "Lade hoch…" : "Hochladen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ReceiptLinkDialog({ period, receipt, open, onOpenChange, onReload }) {
  // Positionen mit Teilzeiträumen/Einzelposten erscheinen je Zeitraum als eigene Zeile
  const positions = (period.positions || []).flatMap((p) => (p.segments || []).length ? p.segments.map((sg) => ({
    ...p, id: `${p.id}::${sg.id}`, _pid: p.id, _sid: sg.id, amount_gross: sg.amount_gross,
    category_label: `${p.category_label || p.category_key} · ${segLabel(sg)}`,
    allocations: (p.allocations || []).filter((al) => al.segment_id === sg.id),
  })) : [p]);
  // Vorbelegen: bestehende Verknüpfungen
  const initialAmounts = {};
  positions.forEach((p) => {
    const a = (p.allocations || []).find((al) => al.receipt_id === receipt.id);
    initialAmounts[p.id] = a ? String(a.amount) : "";
    initialAmounts[`__aid_${p.id}`] = a?.id || null;
  });
  const [amounts, setAmounts] = useState(initialAmounts);
  const [saving, setSaving] = useState(false);

  const currentSum = Object.entries(amounts)
    .filter(([k]) => !k.startsWith("__aid"))
    .reduce((s, [, v]) => s + (parseFloat(v || 0) || 0), 0);
  const total = receipt.total_gross || 0;

  // 2026-02 (iter130) — Workflow-orientierte Zuordnung:
  // Ein Klick reicht, um einer Position den passenden Teilbetrag zuzuweisen.
  // Vorschlagslogik: Zeigt die Position noch keine Belegung, wird der volle
  // Positionsbetrag übernommen (Standardfall). Ist der Belegrest kleiner,
  // wird auf den Rest begrenzt. Ist die Zeile bereits gefüllt → auf 0 zurück.
  const receiptRemaining = (excludePosId = null) => {
    const sumOthers = Object.entries(amounts)
      .filter(([k]) => !k.startsWith("__aid") && k !== excludePosId)
      .reduce((s, [, v]) => s + (parseFloat(v || 0) || 0), 0);
    return Math.max(0, (total || 0) - sumOthers);
  };
  const assignPosition = (p) => {
    const current = parseFloat(amounts[p.id] || 0) || 0;
    if (current > 0) {
      setAmounts({ ...amounts, [p.id]: "" });
      return;
    }
    const positionAmt = parseFloat(p.amount_gross || 0) || 0;
    const remaining = receiptRemaining(p.id);
    let suggested;
    if (total > 0 && positionAmt > 0) {
      // Beides bekannt → Position-Betrag, gedeckelt auf Belegrest
      suggested = Math.min(positionAmt, remaining || positionAmt);
    } else if (positionAmt > 0) {
      suggested = positionAmt;
    } else {
      suggested = remaining || total || 0;
    }
    if (suggested <= 0) return;
    setAmounts({ ...amounts, [p.id]: suggested.toFixed(2) });
  };
  const assignFullReceiptTo = (p) => {
    // Ganzer Beleg auf diese eine Position — löscht alle anderen Zuordnungen dieses Belegs.
    if (!total || total <= 0) {
      // ohne bekannten Belegbetrag → fallback: Positionsbetrag
      assignPosition(p);
      return;
    }
    const next = {};
    positions.forEach((q) => {
      next[q.id] = q.id === p.id ? total.toFixed(2) : "";
      next[`__aid_${q.id}`] = amounts[`__aid_${q.id}`] || null;
    });
    setAmounts(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const p of positions) {
        const newAmt = parseFloat(amounts[p.id] || 0) || 0;
        const existingId = amounts[`__aid_${p.id}`];
        // Fall 1: bestehend, aber jetzt 0 → löschen
        if (existingId && newAmt <= 0) {
          await api.delete(`/bka/allocations/${existingId}`);
          continue;
        }
        // Fall 2: bestehend & unverändert → nichts
        // Fall 3: neu oder geändert → alte löschen + neu anlegen
        if (newAmt > 0) {
          if (existingId) {
            await api.delete(`/bka/allocations/${existingId}`);
          }
          await api.post(`/bka/allocations`, {
            receipt_id: receipt.id, position_id: p._pid || p.id, segment_id: p._sid || null, amount: newAmt,
          });
        }
      }
      toast.success("Verknüpfungen gespeichert");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="receipt-link-dialog">
        <DialogHeader>
          <DialogTitle>Beleg verknüpfen: {receipt.filename}</DialogTitle>
          <DialogDescription>
            Teile den Belegbetrag auf eine oder mehrere Positionen auf.
            {total > 0 && ` Belegbetrag: ${formatEUR(total)}.`}
          </DialogDescription>
        </DialogHeader>
        {positions.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            Keine Positionen vorhanden. Erst Positionen anlegen, dann verknüpfen.
          </div>
        ) : (
          <>
            <div className="text-[11px] text-slate-500 mb-2">
              Tipp: Klicke auf <span className="font-medium">Übernehmen</span>, um den Positionsbetrag
              als Teilbetrag einzutragen. Für einen Beleg, der komplett zu einer Position gehört,
              nutze den <span className="font-medium">Alles-Pfeil</span> in der Zeile.
            </div>
            <div className="max-h-96 overflow-y-auto rounded border border-slate-200">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-medium">Position</th>
                    <th className="text-right px-2 py-1.5 font-medium w-24">Positionsbetrag</th>
                    <th className="text-right px-2 py-1.5 font-medium w-44">Teilbetrag</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {positions.map((p) => {
                    const currentValue = parseFloat(amounts[p.id] || 0) || 0;
                    const isAssigned = currentValue > 0;
                    return (
                    <tr key={p.id} className={isAssigned ? "bg-emerald-50/40" : ""}>
                      <td className="px-2 py-1.5">
                        <div className="font-medium text-xs">{p.category_label || p.category_key}</div>
                        <div className="text-[10px] text-slate-500">
                          bereits belegt: {formatEUR(p.receipt_sum)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs tabular-nums text-slate-600">
                        {formatEUR(p.amount_gross)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => assignFullReceiptTo(p)}
                            title="Ganzer Belegbetrag auf diese Position (andere Zuordnungen leeren)"
                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                            data-testid={`link-assign-full-${p.id}`}
                          >
                            <ArrowsInLineHorizontal size={13} />
                          </button>
                          <Button
                            type="button"
                            size="sm"
                            variant={isAssigned ? "default" : "outline"}
                            onClick={() => assignPosition(p)}
                            className="h-7 px-2 text-[11px]"
                            title={isAssigned ? "Zuordnung leeren" : "Positionsbetrag übernehmen (auf Belegrest begrenzt)"}
                            data-testid={`link-assign-${p.id}`}
                          >
                            {isAssigned ? "Leeren" : "Übernehmen"}
                          </Button>
                          <Input
                            type="number" step="0.01" min="0"
                            className="h-7 text-xs text-right w-24"
                            value={amounts[p.id] || ""}
                            onChange={(e) => setAmounts({ ...amounts, [p.id]: e.target.value })}
                            data-testid={`link-amount-${p.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td colSpan="2" className="px-2 py-2 text-right text-xs font-medium">
                      Summe zugeordnet:
                    </td>
                    <td className="px-2 py-2 text-right text-xs font-medium tabular-nums">
                      <span className={
                        (total > 0 && Math.abs(currentSum - total) < 0.01) ? "text-emerald-700" :
                        (total > 0 && currentSum > total) ? "text-rose-700" : ""
                      }>
                        {formatEUR(currentSum)}
                        {total > 0 && ` / ${formatEUR(total)}`}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || positions.length === 0} data-testid="link-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Einzelabrechnungen
// ============================================================================

// iter153 — Detail-Tabelle: zeigt beim Ausklappen einer Einzelabrechnung alle
// Positionen im gleichen Spalten-Schema wie das PDF (Pos · Kostenart · Ausgaben ges.
// · Schlüssel ges. · Schlüssel-Bez. · Ihr Umlageschlüssel · Zeitanteil · Ihr Anteil).
function SettlementLinesTable({ settlement }) {
  const lines = settlement?.lines || [];
  if (!lines.length) {
    return <div className="text-xs text-slate-500 italic">Keine Positionen in dieser Einzelabrechnung.</div>;
  }
  const totalAmount = lines.reduce((s, l) => s + (l.tenant_amount || 0), 0);
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-700 mb-2">
        Betriebskosten (§ 2 BetrKV) — {lines.length} Position{lines.length > 1 ? "en" : ""}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`settle-lines-${settlement.id}`}>
          <thead className="text-slate-500">
            <tr className="border-b border-slate-200">
              <th className="text-left py-1.5 pr-2 font-normal w-8">Pos</th>
              <th className="text-left py-1.5 pr-2 font-normal">Kostenart</th>
              <th className="text-right py-1.5 pr-2 font-normal">Ausgaben ges.</th>
              <th className="text-right py-1.5 pr-2 font-normal">Schlüssel ges.</th>
              <th className="text-left py-1.5 pr-2 font-normal">Schlüssel-Bez.</th>
              <th className="text-right py-1.5 pr-2 font-normal">Ihr Umlageschlüssel</th>
              <th className="text-right py-1.5 pr-2 font-normal">Zeitanteil</th>
              <th className="text-right py-1.5 pr-2 font-normal tabular-nums">Ihr Anteil</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => {
              const pct = l.umlagefaehig === "teilweise" && l.umlagefaehig_percent
                ? ` (${Math.round(l.umlagefaehig_percent)}%)`
                : "";
              const timeShare = (l.time_share_days && l.period_days && l.time_share_days < l.period_days)
                ? `${l.time_share_days}/${l.period_days}` : "—";
              const isDim = (l.tenant_amount || 0) === 0 && l.distribution_key === "consumption";
              return (
                <tr key={idx} className={`border-b border-slate-100 ${isDim ? "text-slate-400" : "text-slate-700"}`}>
                  <td className="py-1 pr-2 tabular-nums">{idx + 1}</td>
                  <td className="py-1 pr-2">{l.category_label || "—"}{pct}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{formatEUR(l.total_amount || 0)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{fmtKeyTotal(l.distribution_key, l.key_total)}</td>
                  <td className="py-1 pr-2">{DIST_UNIT_LABEL[l.distribution_key] || l.distribution_key || "—"}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{fmtKeyShare(l.distribution_key, l.key_share)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-slate-500">{timeShare}</td>
                  <td className="py-1 pr-2 text-right tabular-nums font-semibold text-slate-900">
                    {isDim ? "—" : formatEUR(l.tenant_amount || 0)}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-slate-300 font-semibold">
              <td className="py-1.5 pr-2" colSpan={7}>Zwischensumme</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{formatEUR(totalAmount)}</td>
            </tr>
          </tbody>
        </table>
        {settlement.vat_rate ? (
          <div className="mt-2 text-xs text-right space-y-0.5" data-testid="settlement-vat-block">
            <div>Kostenanteil netto: <b className="tabular-nums">{formatEUR(settlement.umlage_net)}</b></div>
            <div>zzgl. USt {settlement.vat_rate} %: <b className="tabular-nums">{formatEUR(settlement.vat_amount)}</b></div>
            <div>Kostenanteil brutto: <b className="tabular-nums">{formatEUR(settlement.total_umlage)}</b></div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SettlementsTab({ period, onReload }) {
  const [settlements, setSettlements] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [calculating, setCalculating] = useState(false);
  // 2026-02 (iter116) — MEA-Drift-Detection: wenn Stammdaten seit Berechnung geändert wurden
  const [drift, setDrift] = useState(null);
  // 2026-02 (iter122) — Live-Vorschau der Verteilung (ohne Speichern)
  const [preview, setPreview] = useState(null);
  // iter132.4 — PDF-Bildschirm-Vorschau
  const [previewPdfSid, setPreviewPdfSid] = useState(null);
  // iter150 — VZ-Anpassungs-Dialog
  const [vzAdjustSid, setVzAdjustSid] = useState(null);
  const [previewDoc, setPreviewDoc] = useState("abrechnung");
  const [openUnitId, setOpenUnitId] = useState(null);
  // iter153 — ausklappbare Zeilen mit Einzelpositionen je Einzelabrechnung
  const [expandedSettleId, setExpandedSettleId] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: e }, driftRes, previewRes] = await Promise.all([
        api.get(`/bka/periods/${period.id}/settlements`),
        api.get(`/bka/periods/${period.id}/eligible-tenancies`),
        api.get(`/bka/periods/${period.id}/mea-drift`).catch(() => ({ data: null })),
        api.get(`/bka/periods/${period.id}/distribution-preview`).catch(() => ({ data: null })),
      ]);
      setSettlements(s || []);
      setEligible(e || []);
      setDrift(driftRes?.data || null);
      setPreview(previewRes?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period.id]);

  const calc = async () => {
    setCalculating(true);
    try {
      const { data } = await api.post(`/bka/periods/${period.id}/calculate`);
      toast.success(`${data.settlements_created} Einzelabrechnung(en) erzeugt`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-500">
          Berechnet für jeden Mieter im Zeitraum eine tagesgenaue Einzelabrechnung
          (BGH VIII ZR 340/06) mit Verrechnung der Vorauszahlungen.
        </div>
        <div className="flex items-center gap-2">
          {/* iter135 — Belegverzeichnis-Upload */}
          <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 border border-slate-300 rounded cursor-pointer hover:bg-slate-50"
                 data-testid="upload-belegverzeichnis-btn"
                 title="Belegverzeichnis-PDF wird an jede erzeugte BKA angehängt.">
            <Paperclip size={12} />
            {period?.belegverzeichnis?.filename
              ? <span className="text-emerald-700">📎 {period.belegverzeichnis.filename}</span>
              : "Belegverzeichnis anhängen"}
            <input type="file" accept="application/pdf" className="hidden"
                   onChange={async (e) => {
                     const file = e.target.files?.[0];
                     if (!file) return;
                     const fd = new FormData();
                     fd.append("file", file);
                     try {
                       await api.post(`/bka/periods/${period.id}/belegverzeichnis`, fd,
                                      { headers: { "Content-Type": "multipart/form-data" } });
                       toast.success("Belegverzeichnis hinterlegt – wird an alle BKA angehängt.");
                       load();
                     } catch (err) { toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen"); }
                     e.target.value = "";
                   }} />
          </label>
          {period?.belegverzeichnis?.filename && (
            <button type="button"
                    onClick={async () => {
                      if (!confirm("Belegverzeichnis entfernen?")) return;
                      try {
                        await api.delete(`/bka/periods/${period.id}/belegverzeichnis`);
                        toast.success("Belegverzeichnis entfernt");
                        load();
                      } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
                    }}
                    className="text-xs text-slate-400 hover:text-rose-700"
                    title="Belegverzeichnis entfernen"
                    data-testid="delete-belegverzeichnis-btn">
              <X size={14} />
            </button>
          )}
          {settlements.length > 0 && (
            <>
              <Button size="sm" variant="outline" className="gap-1.5"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/bka/periods/${period.id}/persist-pdfs`);
                          toast.success(`${data.created} neue PDF(s) im Mieter- und Objekt-Ordner abgelegt.`);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}
                      data-testid="persist-pdfs-btn">
                <FileText size={14} /> PDFs in Ordner ablegen
              </Button>
            </>
          )}
          <Button onClick={calc} disabled={calculating} data-testid="calc-btn" className="gap-1.5">
            <Calculator size={14} /> {calculating ? "Berechne…" : (settlements.length > 0 ? "Neu berechnen" : "Berechnen")}
          </Button>
        </div>
      </div>

      {eligible.length > 0 && settlements.length === 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          <b>{eligible.filter((e) => e.eligible).length}</b> berechtigte Mietverhältnisse gefunden.
          Klicke auf „Berechnen“, um die Einzelabrechnungen zu erzeugen.
        </div>
      )}

      {/* 2026-02 (iter122) — Live-Vorschau der Verteilung auf Einheiten
          (rein informativ; wird bei Klick auf „Berechnen" gespeichert). */}
      {preview && (preview.units || []).length > 0 && settlements.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="settlements-preview">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-700">
              Vorschau: Verteilung auf {preview.units.length} Einheit{preview.units.length > 1 ? "en" : ""}
              <span className="ml-2 font-normal text-slate-500">
                Gesamt {formatEUR(preview.positions_total)} · Nenner {preview.total_shares}
              </span>
            </div>
            <div className="text-[10px] text-slate-500 italic">
              Wird beim Klick auf „Berechnen" für alle Mieter gespeichert.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50/50">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-3 py-1.5 font-medium">Einheit</th>
                <th className="text-right px-3 py-1.5 font-medium w-24">MEA</th>
                <th className="text-right px-3 py-1.5 font-medium w-24">Wohnfl.</th>
                <th className="text-right px-3 py-1.5 font-medium w-32 tabular-nums">Umlage-Anteil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {preview.units.map((u) => {
                const eligible = (u.positions || []).filter((p) => p.umlagefaehig !== "nicht" && p.unit_share > 0.001);
                const isOpen = openUnitId === u.unit_id;
                return (
                  <>
                    <tr key={u.unit_id} className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setOpenUnitId(isOpen ? null : u.unit_id)}
                        data-testid={`preview-unit-${u.unit_id}`}>
                      <td className="px-2 py-1.5 text-slate-400">{isOpen ? "▾" : "▸"}</td>
                      <td className="px-3 py-1.5 font-mono">{u.unit_number || u.unit_id.slice(0, 8)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {u.weg_share && u.weg_share_denominator
                          ? `${u.weg_share}/${u.weg_share_denominator}`
                          : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{u.living_area ? `${u.living_area} m²` : "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-800">{formatEUR(u.total_share)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/70">
                        <td></td>
                        <td colSpan={4} className="px-3 py-2">
                          <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
                            Positionen für {u.unit_number} · {eligible.length} umlagefähig
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="text-left py-1">Position</th>
                                <th className="text-left py-1 w-32">Schlüssel</th>
                                <th className="text-right py-1 w-24">Gesamt</th>
                                <th className="text-right py-1 w-24">Anteil</th>
                                <th className="text-right py-1 w-24">Betrag</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(u.positions || []).map((p) => (
                                <tr key={p.position_id} className="border-t border-slate-200">
                                  <td className="py-1">{p.category_label || p.category_key}</td>
                                  <td className="py-1 text-slate-500">{DIST_LABEL[p.distribution_key] || p.distribution_key}</td>
                                  <td className="py-1 text-right tabular-nums">{formatEUR(p.position_total)}</td>
                                  <td className="py-1 text-right tabular-nums text-slate-500">
                                    {p.key_total > 0 ? `${(p.key_share / p.key_total * 100).toFixed(2)} %` : "—"}
                                  </td>
                                  <td className={`py-1 text-right tabular-nums font-semibold ${p.umlagefaehig === "nicht" ? "text-slate-400 line-through" : "text-slate-800"}`}>
                                    {formatEUR(p.unit_share)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td></td>
                <td className="px-3 py-2" colSpan={3}>Summe (nur volle Umlage)</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEUR(preview.units.reduce((s, u) => s + (u.total_share || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* 2026-02 (iter116) — MEA-Drift-Warnung: Stammdaten wurden nach Kalkulation geändert */}
      {drift?.has_drift && settlements.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="mea-drift-warning">
          <div className="font-semibold flex items-center gap-1.5 mb-1">
            ⚠ Miteigentumsanteile wurden nach der letzten Berechnung geändert
          </div>
          <div className="text-[11px] text-amber-800 mb-1.5">
            {drift.changes.length} Einheit(en) betroffen. Bestehende Einzelabrechnungen basieren noch auf dem alten Stand
            (Snapshot vom {drift.snapshot_at ? new Date(drift.snapshot_at).toLocaleString("de-DE") : "?"}).
            Klicke auf „Neu berechnen“, um die aktuellen MEA-Werte anzuwenden.
          </div>
          <details className="text-[11px] text-amber-800 mb-2">
            <summary className="cursor-pointer font-medium">Änderungen anzeigen ({drift.changes.length})</summary>
            <ul className="mt-1 space-y-0.5 pl-4 list-disc">
              {drift.changes.map((c) => (
                <li key={c.unit_id}>
                  <span className="font-mono">{c.unit_label || c.unit_id.slice(0, 8)}</span>: {" "}
                  <span className="font-mono">{c.was.weg_share ?? "?"}/{c.was.weg_share_denominator || c.was.total_shares}</span>
                  {" → "}
                  <span className="font-mono">{c.now.weg_share ?? "?"}/{c.now.weg_share_denominator || c.now.total_shares}</span>
                </li>
              ))}
            </ul>
          </details>
          <Button
            size="sm"
            onClick={calc}
            disabled={calculating}
            className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 gap-1.5"
            data-testid="mea-drift-recalc-btn"
          >
            <Calculator size={12} /> {calculating ? "Berechne …" : "Jetzt neu berechnen"}
          </Button>
        </div>
      )}

      {settlements.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr>
                <th className="w-6"></th>
                <th className="text-left px-3 py-2 font-medium">Mieter</th>
                <th className="text-left px-3 py-2 font-medium w-24">Einheit</th>
                <th className="text-left px-3 py-2 font-medium w-36">Zeitraum</th>
                <th className="text-right px-3 py-2 font-medium w-28">Umlage-Anteil</th>
                <th className="text-right px-3 py-2 font-medium w-28">Vorauszahlung</th>
                <th className="text-right px-3 py-2 font-medium w-28">Saldo</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {settlements.map((s) => {
                const isNach = s.balance > 0.01;
                const isExpanded = expandedSettleId === s.id;
                // iter132.4 — Warnungen erkennen (aus lines ableitbar)
                const zeroConsumptionLines = ((s.lines || []).filter(
                  (l) => l.distribution_key === "consumption" && (l.tenant_amount || 0) === 0 && (l.total_amount || 0) > 0
                ));
                const hasConsumptionWarning = zeroConsumptionLines.length > 0;
                return (
                  <Fragment key={s.id}>
                  <tr data-testid={`settle-row-${s.id}`}
                      className={hasConsumptionWarning ? "bg-amber-50/50" : ""}>
                    <td className="px-1 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => setExpandedSettleId(isExpanded ? null : s.id)}
                        className="text-slate-500 hover:text-slate-900 transition"
                        title={isExpanded ? "Positionen ausblenden" : "Positionen anzeigen"}
                        data-testid={`settle-toggle-${s.id}`}
                      >
                        {isExpanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {s.tenant_name}
                      {s.is_vacancy && (
                        <span className="ml-2 inline-flex items-center rounded border border-sky-300 bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800"
                              title={`Einheit stand vom ${formatDate(s.vacancy_from)} bis ${formatDate(s.vacancy_to)} leer – Abrechnung an Eigentümer.`}
                              data-testid={`settle-vacancy-${s.id}`}>
                          🏚 Leerstand → Eigentümer
                        </span>
                      )}
                      {hasConsumptionWarning && (
                        <span
                          className="ml-2 inline-flex items-center rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                          title={`Verbrauchspositionen ohne Zählerstände: ${zeroConsumptionLines.map((l) => l.category_label).join(", ")}. Diese Positionen sind mit 0 € umgelegt — bitte Verbrauchswerte nachtragen und neu berechnen.`}
                          data-testid={`settle-warn-${s.id}`}
                        >
                          ⚠ Verbrauch fehlt
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.unit_label || "—"}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">
                      {formatDate(s.tenancy_from)} – {formatDate(s.tenancy_to)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatEUR(s.total_umlage)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600"
                        title={
                          (s.prepayments_utilities || s.prepayments_heating)
                            ? `NK-Vorauszahlung: ${formatEUR(s.prepayments_utilities || 0)}\nHeizungsvorauszahlung: ${formatEUR(s.prepayments_heating || 0)}`
                            : undefined
                        }>
                      {formatEUR(s.prepayments_sum)}
                      {(s.prepayments_utilities > 0 || s.prepayments_heating > 0) && (
                        <div className="text-[10px] text-slate-500 tabular-nums">
                          NK {formatEUR(s.prepayments_utilities || 0)} · Heiz {formatEUR(s.prepayments_heating || 0)}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${isNach ? "text-rose-700" : "text-emerald-700"}`}>
                      {isNach ? "+" : ""}{formatEUR(s.balance)}
                      {/* iter150 — VZ-Anpassungs-Vorschlag bei signifikantem Delta */}
                      {s.is_significant_delta && s.suggested_new_prepayment && !s.prepayment_adjusted && (
                        <div className="mt-1 text-[10px] font-normal text-left" data-testid={`settle-vz-suggest-${s.id}`}>
                          <button
                            type="button"
                            onClick={() => setVzAdjustSid(s.id)}
                            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-amber-900 hover:bg-amber-200 transition"
                            title={`Bisherige VZ: ${formatEUR(s.current_prepayment_month)}/Monat · Vorschlag: ${formatEUR(s.suggested_new_prepayment)}/Monat`}
                          >
                            💡 VZ anpassen: {formatEUR(s.suggested_new_prepayment)}/Mo
                          </button>
                        </div>
                      )}
                      {s.prepayment_adjusted && (
                        <div className="mt-1 text-[10px] font-normal text-emerald-700" title={`Angepasst ab ${s.prepayment_adjusted_effective_from}`}>
                          ✓ VZ angepasst auf {formatEUR(s.prepayment_adjusted_to_eur)}/Mo
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                onClick={() => { setPreviewPdfSid(s.id); setPreviewDoc && setPreviewDoc("letter"); }}
                                data-testid={`settle-letter-${s.id}`}
                                title="Anschreiben Vorschau">
                          <Eye size={12} /> Anschreiben
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                onClick={() => { setPreviewPdfSid(s.id); setPreviewDoc && setPreviewDoc("abrechnung"); }}
                                data-testid={`settle-preview-${s.id}`}
                                title="Abrechnung Vorschau">
                          <Eye size={12} /> Abrechnung
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50">
                      <td colSpan={8} className="px-6 py-3">
                        <SettlementLinesTable settlement={s} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 text-xs">
              <tr>
                <td colSpan="4" className="px-3 py-2 text-right font-medium">Summen:</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">
                  {formatEUR(settlements.reduce((s, x) => s + x.total_umlage, 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEUR(settlements.reduce((s, x) => s + x.prepayments_sum, 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">
                  {formatEUR(settlements.reduce((s, x) => s + x.balance, 0))}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div className="px-3 py-2 border-t bg-slate-50/60 text-[11px] text-slate-500 flex items-center gap-1.5"
               data-testid="pdf-hint">
            <FileText size={12} className="text-slate-400" />
            Die PDF-Abrechnungen werden im Dokumentenordner des jeweiligen Mieters bzw.
            Eigentümers abgelegt. Über &bdquo;PDFs in Ordner ablegen&ldquo; kannst du diese jederzeit neu erzeugen.
          </div>
        </div>
      )}
      {settlements.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
          Noch keine Einzelabrechnungen. Klicke „Berechnen“, sobald alle Positionen erfasst sind.
        </div>
      )}
      {previewPdfSid && (
        <PdfPreviewDialog
          settlement={settlements.find((x) => x.id === previewPdfSid)}
          docKind={previewDoc}
          onClose={() => setPreviewPdfSid(null)}
        />
      )}
      {vzAdjustSid && (
        <VzAdjustDialog
          settlement={settlements.find((x) => x.id === vzAdjustSid)}
          onClose={() => setVzAdjustSid(null)}
          onDone={() => { setVzAdjustSid(null); load && load(); }}
        />
      )}

    </div>
  );
}

// iter150 — Vorauszahlungs-Anpassungs-Dialog (Bestätigung des Systemvorschlags)
function VzAdjustDialog({ settlement, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [newVz, setNewVz] = useState(settlement?.suggested_new_prepayment ?? "");
  if (!settlement) return null;
  // Effective = 1. des Folgemonats
  const today = new Date();
  const eff = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const effIso = `${eff.getFullYear()}-${String(eff.getMonth() + 1).padStart(2, "0")}-01`;
  const effLabel = eff.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  const fmt = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bka/settlements/${settlement.id}/apply-prepayment-adjust`, {
        new_prepayment: parseFloat(newVz),
        effective_date: effIso,
      });
      toast.success("Vorauszahlung wurde angepasst und im MV eingetragen");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anpassung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vz-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Vorauszahlung anpassen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            <p className="font-medium mb-1">Signifikantes Abrechnungs-Delta erkannt (&gt; 15 %)</p>
            <p className="text-xs">
              Aktuelle VZ (NK + Heizung): <strong>{fmt(settlement.current_prepayment_month)}/Monat</strong> ·
              Saldo dieser Abrechnung: <strong className={settlement.balance > 0 ? "text-rose-700" : "text-emerald-700"}>
                {settlement.balance > 0 ? "+" : ""}{fmt(settlement.balance)}</strong>.
              Um zukünftige Abrechnungen ausgewogener zu halten, schlagen wir eine Anpassung vor.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Neue Vorauszahlung (€/Monat)</Label>
              <Input type="number" step="1" min="0" value={newVz}
                     onChange={(e) => setNewVz(e.target.value)}
                     data-testid="vz-new-input" />
              <div className="text-[11px] text-slate-500 mt-1">
                Systemvorschlag: {fmt(settlement.suggested_new_prepayment)} (aufgerundet)
              </div>
            </div>
            <div>
              <Label className="text-xs">Wirksam ab</Label>
              <div className="h-9 px-3 flex items-center bg-slate-50 border border-slate-300 rounded text-sm">
                {effLabel}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                1. des Folgemonats (automatisch)
              </div>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700">
            Bestätigen Sie, wird im Mietverhältnis eine Vertragsänderung
            (<em>Nebenkosten-Vorauszahlung</em>) angelegt und alle offenen
            Sollstellungen ab dem <strong>{effLabel}</strong> automatisch
            regeneriert. Das Anschreiben der nächsten BKA weist dann den
            Passus „Ab {effLabel} beträgt Ihre neue Vorauszahlung …" aus.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !newVz || parseFloat(newVz) < 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="vz-confirm-btn">
            {busy ? "Speichere …" : "Anpassung bestätigen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter132.4 — Bildschirm-Vorschau der Settlement-PDF
function PdfPreviewDialog({ settlement, docKind = "abrechnung", onClose }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let created = null;
    (async () => {
      try {
        const u = await fetchPdfBlobUrl(`/bka/settlements/${settlement.id}/pdf?doc=${docKind}`);
        created = u;
        setUrl(u);
      } catch (e) {
        setError(e?.response?.data?.detail || "PDF konnte nicht geladen werden");
      }
    })();
    return () => { if (created) URL.revokeObjectURL(created); };
  }, [settlement.id, docKind]);
  const zeroLines = ((settlement.lines || []).filter(
    (l) => l.distribution_key === "consumption" && (l.tenant_amount || 0) === 0 && (l.total_amount || 0) > 0
  ));
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-4" data-testid="pdf-preview-dialog">
        <DialogHeader className="pb-2 shrink-0">
          <DialogTitle className="text-base">
            Vorschau {docKind === "letter" ? "Anschreiben" : "Abrechnung"} — {settlement.tenant_name} · {settlement.unit_label || "—"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Endgültiger PDF-Zustand vor dem Versand. Bei Bedarf schließen, Positionen anpassen und neu berechnen.
          </DialogDescription>
        </DialogHeader>
        {zeroLines.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 shrink-0">
            <div className="font-semibold flex items-center gap-1.5">
              ⚠ Verbrauchspositionen ohne Zählerstände
            </div>
            <div className="mt-0.5">
              Folgende Positionen haben den Umlageschlüssel &bdquo;Verbrauch&ldquo;, aber es liegen keine Werte je Einheit vor —
              sie werden derzeit mit 0,00 € umgelegt:
              <ul className="list-disc pl-4 mt-1">
                {zeroLines.map((l, i) => (
                  <li key={i}><span className="font-medium">{l.category_label}</span> — Ausgaben {formatEUR(l.total_amount)}</li>
                ))}
              </ul>
              Bitte in der Kostenposition die Verbrauchswerte je Einheit erfassen und die Berechnung wiederholen,
              bevor Sie an den Mieter versenden.
            </div>
          </div>
        )}
        <div className="flex-1 rounded border border-slate-200 bg-slate-100 overflow-hidden mt-2">
          {error && <div className="p-4 text-sm text-rose-700">{error}</div>}
          {!error && !url && <div className="p-4 text-sm text-slate-500">Lade PDF …</div>}
          {url && (
            <iframe
              src={url}
              title="PDF-Vorschau"
              className="w-full h-full border-0"
              data-testid="pdf-preview-frame"
            />
          )}
        </div>
        <DialogFooter className="pt-2 shrink-0">
          <Button variant="outline" onClick={() => openAuthenticatedPdf(`/bka/settlements/${settlement.id}/pdf?doc=${docKind === "letter" ? "letter" : "abrechnung"}`)}>
            <FilePdf size={14} className="mr-1.5" /> In neuem Tab öffnen
          </Button>
          <Button onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// SEG-Import-Assistent
// ============================================================================
function SegImportTab({ period, property, catalog, onReload }) {
  const [meaNumer, setMeaNumer] = useState("");
  const [meaDenom, setMeaDenom] = useState(property?.weg_total_shares || 1000);
  const [reference, setReference] = useState(period.external_weg_reference || "");
  const [lines, setLines] = useState([
    { category_key: "1_grundsteuer", total_amount_full: "", umlagefaehig: "voll", umlagefaehig_percent: null, notes: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const isDraft = period.status === "draft";
  const allCats = [...(catalog?.betrkv || []), ...(catalog?.weg_extra || [])];

  useEffect(() => {
    if (period.seg_unit_ids && period.seg_unit_ids[0] && property) {
      const uid = period.seg_unit_ids[0];
      const u = (property.units || []).find((x) => x.id === uid);
      if (u?.weg_share) setMeaNumer(String(u.weg_share));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLine = () => {
    setLines([...lines, {
      category_key: "15_sonstige", total_amount_full: "", umlagefaehig: "voll",
      umlagefaehig_percent: null, notes: "",
    }]);
  };
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const patch = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const meaPct = (meaNumer && meaDenom) ? (parseFloat(meaNumer) / parseFloat(meaDenom) * 100) : null;
  const preview = lines.map((l) => {
    const full = parseFloat(l.total_amount_full || 0);
    const own = (meaNumer && meaDenom)
      ? Math.round(full * (parseFloat(meaNumer) / parseFloat(meaDenom)) * 100) / 100
      : null;
    return { ...l, own_calc: own };
  });
  const previewSum = preview.reduce((s, l) => s + (l.own_calc || 0), 0);

  const doImport = async () => {
    if (!meaNumer || !meaDenom) { toast.error("MEA-Anteil erforderlich"); return; }
    const validLines = lines.filter((l) => l.category_key && l.total_amount_full);
    if (validLines.length === 0) { toast.error("Mindestens eine Zeile mit Kategorie und Betrag"); return; }
    setSaving(true);
    try {
      await api.post(`/bka/periods/${period.id}/import-weg`, {
        positions: validLines.map((l, i) => ({
          category_key: l.category_key,
          total_amount_full: parseFloat(l.total_amount_full),
          umlagefaehig: l.umlagefaehig,
          umlagefaehig_percent: l.umlagefaehig === "teilweise" ? parseFloat(l.umlagefaehig_percent || 100) : null,
          notes: l.notes || null,
          sort_order: i * 10,
        })),
        external_weg_reference: reference,
        mea_share_numer: parseInt(meaNumer),
        mea_share_denom: parseInt(meaDenom),
      });
      toast.success(`${validLines.length} Positionen aus WEG-Abrechnung übernommen`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900">
        <b>SEG-Import-Assistent</b> — trage hier die Positionen aus der Jahresabrechnung der
        fremd-verwalteten WEG ein. Der Server berechnet deinen Anteil automatisch anhand deiner MEA.
        <br />
        Vorherige Positionen dieser Periode werden bei erneutem Import überschrieben.
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>MEA-Zähler (deine Wohnung)</Label>
            <Input type="number" value={meaNumer} onChange={(e) => setMeaNumer(e.target.value)}
                   placeholder="z. B. 156" data-testid="seg-mea-numer" disabled={!isDraft} />
          </div>
          <div>
            <Label>MEA-Nenner (WEG gesamt)</Label>
            <Input type="number" value={meaDenom} onChange={(e) => setMeaDenom(e.target.value)}
                   placeholder="1000" data-testid="seg-mea-denom" disabled={!isDraft} />
          </div>
          <div>
            <Label>Berechneter Anteil</Label>
            <div className="h-9 px-3 flex items-center text-sm font-medium tabular-nums bg-slate-100 rounded">
              {meaPct != null ? `${meaPct.toFixed(3)} %` : "—"}
            </div>
          </div>
        </div>
        <div>
          <Label>Referenz fremde WEG-Abrechnung</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)}
                 placeholder="z. B. Hausverwaltung XYZ, Jahresabrechnung 2024 vom 15.03.2025"
                 data-testid="seg-reference" disabled={!isDraft} />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs font-medium">Positionen der WEG-Abrechnung</div>
          {isDraft && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addLine}
                    data-testid="seg-add-line">
              <Plus size={12} /> Zeile
            </Button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Kategorie</th>
              <th className="text-right px-2 py-1.5 font-medium w-28">Gesamt WEG</th>
              <th className="text-left px-2 py-1.5 font-medium w-32">Umlage</th>
              <th className="text-right px-2 py-1.5 font-medium w-28">Dein Anteil</th>
              <th className="text-left px-2 py-1.5 font-medium">Notiz</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {preview.map((l, i) => (
              <tr key={i} data-testid={`seg-line-${i}`}>
                <td className="px-2 py-1.5">
                  <Select value={l.category_key} onValueChange={(v) => patch(i, "category_key", v)} disabled={!isDraft}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-96">
                      <div className="text-[10px] px-2 py-1 uppercase text-slate-400">BetrKV § 2</div>
                      {(catalog?.betrkv || []).map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                      <div className="text-[10px] px-2 py-1 uppercase text-slate-400 border-t">WEG-Zusatz</div>
                      {(catalog?.weg_extra || []).map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1.5">
                  <Input type="number" step="0.01" value={l.total_amount_full}
                         onChange={(e) => patch(i, "total_amount_full", e.target.value)}
                         className="h-8 text-xs text-right"
                         data-testid={`seg-full-${i}`} disabled={!isDraft} />
                </td>
                <td className="px-2 py-1.5">
                  <Select value={l.umlagefaehig} onValueChange={(v) => patch(i, "umlagefaehig", v)} disabled={!isDraft}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voll">Voll</SelectItem>
                      <SelectItem value="teilweise">Teilweise</SelectItem>
                      <SelectItem value="nicht">Nicht</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1.5 text-right text-xs tabular-nums font-medium">
                  {l.own_calc != null ? formatEUR(l.own_calc) : "—"}
                </td>
                <td className="px-2 py-1.5">
                  <Input value={l.notes || ""} onChange={(e) => patch(i, "notes", e.target.value)}
                         className="h-8 text-xs" data-testid={`seg-notes-${i}`} disabled={!isDraft} />
                </td>
                <td className="px-2 py-1.5 text-right">
                  {isDraft && lines.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600"
                            onClick={() => removeLine(i)} data-testid={`seg-del-${i}`}>
                      <X size={12} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50">
            <tr>
              <td colSpan="3" className="px-2 py-2 text-right text-xs font-medium">Summe dein Anteil:</td>
              <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums">{formatEUR(previewSum)}</td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {isDraft && (
        <div className="flex justify-end">
          <Button onClick={doImport} disabled={saving || !meaNumer || !meaDenom}
                  className="gap-1.5" data-testid="seg-import-btn">
            <ArrowClockwise size={14} /> {saving ? "Importiere…" : "In BKA übernehmen"}
          </Button>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// iter113 — Korrektur-Assistent + Bulk-Versand + Verbrauchs-Import
// ============================================================================

function CorrectionButton({ period, onReload }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Grund erforderlich"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/bka/periods/${period.id}/create-correction`, {
        correction_reason: reason, copy_positions: true, copy_receipts: true,
      });
      toast.success("Korrektur-BKA angelegt");
      window.location.href = `/betriebskosten/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="bka-correction-btn">
          <ArrowClockwise size={14} /> Korrektur
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="bka-correction-dialog">
        <DialogHeader>
          <DialogTitle>Korrektur-BKA anlegen</DialogTitle>
          <DialogDescription>
            Erzeugt eine neue Periode als Korrektur mit Referenz auf das Original.
            Positionen und Belege werden kopiert. Nach Neuberechnung wird die
            Differenz zum Original je Mieter automatisch ausgewiesen.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Grund der Korrektur</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="z. B. Nachgereichter Beleg Hausmeister 2024"
                    data-testid="bka-correction-reason" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !reason.trim()} data-testid="bka-correction-submit">
            {saving ? "Erzeuge…" : "Korrektur anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DispatchButton({ period, onReload }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [skipSent, setSkipSent] = useState(true);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    try {
      const { data } = await api.get(`/bka/periods/${period.id}/dispatch-preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  useEffect(() => { if (open) loadPreview(); /* eslint-disable-next-line */ }, [open]);

  const dispatch = async () => {
    setSending(true);
    try {
      const payload = { skip_already_sent: skipSent };
      if (testMode && testEmail) payload.test_recipient = testEmail;
      const { data } = await api.post(`/bka/periods/${period.id}/dispatch`, payload);
      toast.success(`${data.sent} versendet · ${data.no_email} ohne E-Mail · ${data.skipped} übersprungen`);
      setOpen(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="bka-dispatch-btn">
          <FilePdf size={14} /> An alle Mieter
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="bka-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>Bulk-Versand an alle Mieter</DialogTitle>
          <DialogDescription>
            PDFs werden erzeugt und per E-Mail als Anhang versandt.
            Ohne E-Mail-Adresse wird die PDF nur im Portal abgelegt.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-4 text-slate-500 text-sm text-center">Lade Vorschau…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Kpi label="Mieter gesamt" value={preview.total} />
              <Kpi label="Mit E-Mail" value={preview.with_email} />
              <Kpi label="Bereits versendet" value={preview.already_sent} />
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1">Mieter</th>
                    <th className="text-left px-2 py-1">E-Mail</th>
                    <th className="text-right px-2 py-1">Saldo</th>
                    <th className="text-center px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(preview.recipients || []).map((r) => (
                    <tr key={r.settlement_id}>
                      <td className="px-2 py-1">{r.tenant_name}</td>
                      <td className="px-2 py-1 text-slate-600">{r.email || <span className="text-rose-600">— fehlt —</span>}</td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {r.balance > 0.01 ? "+" : ""}{formatEUR(r.balance || 0)}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {r.already_sent_at ? <span className="text-emerald-700">✓</span> : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-2 pt-2 border-t">
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={skipSent} onCheckedChange={setSkipSent} data-testid="skip-sent" />
                Bereits versendete überspringen
              </label>
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={testMode} onCheckedChange={setTestMode} data-testid="test-mode" />
                Testversand an eine Adresse
              </label>
              {testMode && (
                <Input type="email" placeholder="test@example.com" value={testEmail}
                       onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email" />
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button onClick={dispatch} disabled={sending || !preview?.can_dispatch}
                  className="bg-emerald-700 hover:bg-emerald-800" data-testid="bka-dispatch-send">
            {sending ? "Versende…" : (testMode ? "Testversand starten" : "An alle Mieter senden")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConsumptionCsvButton({ periodId, onReload }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const { data } = await api.post(`/bka/periods/${periodId}/consumption-import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  const doImport = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const { data } = await api.post(`/bka/periods/${periodId}/consumption-import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.matched} Verbräuche importiert · ${data.positions_updated} Positionen aktualisiert`);
      setOpen(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid="csv-import-btn">
          <Upload size={14} /> Verbrauchs-CSV
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle>Verbrauchsdaten aus CSV importieren</DialogTitle>
          <DialogDescription>
            Format: <code>unit_id ODER unit_number, category_key, verbrauch, einheit</code>
            <br />z. B. <code>1.OG rechts,2_wasser,42.5,m³</code>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }}
                 data-testid="csv-file" />
          {preview && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-y-auto">
              <div className="font-medium">Vorschau: {preview.matched} passende Zeilen{preview.unmatched?.length ? `, ${preview.unmatched.length} nicht zuordenbar` : ""}</div>
              {(preview.unmatched || []).map((u, i) => (
                <div key={i} className="text-rose-700 mt-1">Zeile {u.row}: {u.reason}</div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          {!preview ? (
            <Button onClick={doPreview} disabled={!file || loading} data-testid="csv-preview-btn">
              {loading ? "Prüfe…" : "Vorschau anzeigen"}
            </Button>
          ) : (
            <Button onClick={doImport} disabled={loading || preview.matched === 0}
                    className="bg-emerald-700 hover:bg-emerald-800" data-testid="csv-import-do">
              {loading ? "Importiere…" : `${preview.matched} Zeilen importieren`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// 2026-02-24 · Eigentümer-Kenntnisnahme (Sammel-Mail je Eigentümer)
function NotifyOwnersButton({ period }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const loadPreview = async () => {
    try {
      const { data } = await api.get(`/bka/periods/${period.id}/notify-owners-preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden der Vorschau");
    }
  };
  useEffect(() => { if (open) loadPreview(); /* eslint-disable-next-line */ }, [open]);

  const send = async () => {
    setSending(true);
    try {
      const payload = {};
      if (testMode && testEmail) payload.test_recipient = testEmail;
      const { data } = await api.post(`/bka/periods/${period.id}/notify-owners`, payload);
      toast.success(`${data.sent} Eigentümer benachrichtigt · ${data.no_email} ohne E-Mail`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Versand");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="bka-notify-owners-btn">
          <UsersFour size={14} /> Eigentümer informieren
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="bka-notify-owners-dialog">
        <DialogHeader>
          <DialogTitle>Eigentümer-Kenntnisnahme</DialogTitle>
          <DialogDescription>
            Sammel-Mail je Eigentümer mit einer Tabelle über alle betroffenen Einheiten.
            So werden Eigentümer mit mehreren Einheiten nicht mehrfach angeschrieben.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-4 text-slate-500 text-sm text-center">Lade Vorschau…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Kpi label="Eigentümer" value={preview.groups?.length || 0} />
              <Kpi label="Mit E-Mail" value={(preview.groups || []).filter((g) => g.owner_email).length} />
              <Kpi label="Ohne E-Mail" value={(preview.groups || []).filter((g) => !g.owner_email).length} />
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1.5">Eigentümer</th>
                    <th className="text-left px-2 py-1.5">E-Mail</th>
                    <th className="text-right px-2 py-1.5">Einheiten</th>
                    <th className="text-left px-2 py-1.5">Betroffene Einheiten</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(preview.groups || []).map((g) => (
                    <tr key={g.owner_contact_id} data-testid={`notify-owner-row-${g.owner_contact_id}`}>
                      <td className="px-2 py-1.5 font-medium">{g.owner_name}</td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {g.owner_email || <span className="text-rose-600">— fehlt —</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{g.units_count}</td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {(g.units || []).map((u) => u.unit_label).filter(Boolean).join(", ")}
                      </td>
                    </tr>
                  ))}
                  {(preview.groups || []).length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-500">
                      Keine Eigentümer-Zuordnungen gefunden.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="space-y-2 pt-2 border-t">
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={testMode} onCheckedChange={setTestMode} data-testid="notify-test-mode" />
                Testversand an eine Adresse
              </label>
              {testMode && (
                <Input type="email" placeholder="test@example.com" value={testEmail}
                       onChange={(e) => setTestEmail(e.target.value)} data-testid="notify-test-email" />
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button onClick={send} disabled={sending || !preview?.groups?.length}
                  className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="bka-notify-owners-send">
            <EnvelopeSimple size={14} /> {sending ? "Versende…" : (testMode ? "Testversand starten" : "Sammel-Mails senden")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// 2026-02-24 · iter173 — Batch-VZ-Anpassung (Slider auf Perioden-Ebene)
function BatchVzAdjustButton({ period, onReload }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("to_suggested");
  const [value, setValue] = useState(10);         // %-Default
  const [absolute, setAbsolute] = useState(25);    // EUR-Default
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const payload = {
        mode,
        value: mode === "percent" ? Number(value) : (mode === "absolute" ? Number(absolute) : null),
        only_with_balance: true,
      };
      const { data } = await api.post(`/bka/periods/${period.id}/batch-adjust-prepayments-preview`, payload);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Vorschau");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) loadPreview(); /* eslint-disable-next-line */ }, [open, mode, value, absolute]);

  const execute = async () => {
    if (!confirm(`Wirklich ${preview?.count || 0} Mietverhältnisse per Massen-Anpassung aktualisieren?`)) return;
    setExecuting(true);
    try {
      const payload = {
        mode,
        value: mode === "percent" ? Number(value) : (mode === "absolute" ? Number(absolute) : null),
        only_with_balance: true,
      };
      const { data } = await api.post(`/bka/periods/${period.id}/batch-adjust-prepayments-execute`, payload);
      toast.success(`${data.applied}/${data.total} VZ-Anpassungen angewendet`);
      setOpen(false);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anwenden");
    } finally { setExecuting(false); }
  };

  const totalDelta = (preview?.plan || []).reduce((s, p) => s + (p.delta_eur || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="bka-batch-vz-btn">
          <ArrowsInLineHorizontal size={14} /> VZ-Massen-Anpassung
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="bka-batch-vz-dialog">
        <DialogHeader>
          <DialogTitle>Vorauszahlungen zentral anpassen</DialogTitle>
          <DialogDescription>
            Passt die Betriebs-/Nebenkosten-Vorauszahlung aller Mietverhältnisse dieser BKA-Periode auf einen Schlag an.
            Je MV wird eine Änderungs-Vereinbarung (Amendment) zum 1. des Folgemonats angelegt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pb-2 border-b">
          <div>
            <label className="text-xs font-semibold text-slate-600">Anpassungs-Modus</label>
            <div className="flex flex-wrap gap-3 mt-1 text-sm">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={mode === "to_suggested"} onChange={() => setMode("to_suggested")} data-testid="batch-mode-suggested" />
                <span>auf <b>Domivus-Empfehlung</b> (empfohlen)</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={mode === "percent"} onChange={() => setMode("percent")} data-testid="batch-mode-percent" />
                <span>prozentual</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={mode === "absolute"} onChange={() => setMode("absolute")} data-testid="batch-mode-absolute" />
                <span>EUR-Betrag auf aktuelle VZ addieren</span>
              </label>
            </div>
          </div>
          {mode === "percent" && (
            <div>
              <label className="text-xs font-semibold text-slate-600 flex items-center justify-between">
                <span>Anpassung in Prozent</span>
                <span className="font-mono text-slate-800">{value > 0 ? "+" : ""}{value} %</span>
              </label>
              <input type="range" min="-50" max="100" step="1" value={value}
                     onChange={(e) => setValue(Number(e.target.value))}
                     className="w-full" data-testid="batch-slider-percent" />
            </div>
          )}
          {mode === "absolute" && (
            <div>
              <label className="text-xs font-semibold text-slate-600 flex items-center justify-between">
                <span>Monatlicher Zuschlag/Abschlag</span>
                <span className="font-mono text-slate-800">{absolute > 0 ? "+" : ""}{absolute} €</span>
              </label>
              <input type="range" min="-200" max="500" step="5" value={absolute}
                     onChange={(e) => setAbsolute(Number(e.target.value))}
                     className="w-full" data-testid="batch-slider-absolute" />
            </div>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500">Lade Vorschau…</div>
        ) : preview ? (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 gap-2 text-xs py-2">
              <Kpi label="Mietverhältnisse" value={preview.count} />
              <Kpi label="Summe Δ monatlich" value={`${totalDelta > 0 ? "+" : ""}${totalDelta.toFixed(2)} €`} />
              <Kpi label="Jahres-Mehreinnahme" value={`${totalDelta > 0 ? "+" : ""}${(totalDelta * 12).toFixed(2)} €`} />
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Vertrag</th>
                  <th className="text-left px-2 py-1.5">Mieter</th>
                  <th className="text-right px-2 py-1.5">Saldo</th>
                  <th className="text-right px-2 py-1.5">Aktuelle VZ</th>
                  <th className="text-right px-2 py-1.5">Neue VZ</th>
                  <th className="text-right px-2 py-1.5">Δ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(preview.plan || []).map((it) => (
                  <tr key={it.settlement_id} data-testid={`batch-row-${it.settlement_id}`}>
                    <td className="px-2 py-1.5 font-mono">{it.contract_number || "—"}</td>
                    <td className="px-2 py-1.5">{it.tenant_name} <span className="text-slate-400">· {it.unit_label}</span></td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${it.balance > 0 ? "text-rose-700" : it.balance < 0 ? "text-emerald-700" : ""}`}>
                      {it.balance > 0 ? "+" : ""}{it.balance.toFixed(2)} €
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{it.current_total_vz.toFixed(2)} €</td>
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{it.new_vz.toFixed(2)} €</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${it.delta_eur > 0 ? "text-emerald-700" : it.delta_eur < 0 ? "text-rose-700" : ""}`}>
                      {it.delta_eur > 0 ? "+" : ""}{it.delta_eur.toFixed(2)} €
                    </td>
                  </tr>
                ))}
                {(preview.plan || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-500">
                    Keine Settlements mit offenem Saldo. Modus ggf. umstellen.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={execute} disabled={executing || !preview?.count}
                  className="bg-slate-900 hover:bg-slate-800" data-testid="bka-batch-vz-execute">
            {executing ? "Wende an…" : `${preview?.count || 0} Mietverhältnisse anpassen`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

